import { create } from 'zustand';
import notification from '../utils/notification';
import { isTestVersionActive } from './redesignExperiment';
import { 
  updateBundle, 
  createOnetimeItem, 
  deleteOnetime, 
  updateOnetime, 
  fetchSubscription, 
  fetchOnetimes,
  updateBundleSelectionItems
} from './recharge';
import { trackBoxSelectionSaved, logPortalError } from './api';
import { isErrorChargeBlockingError, ERROR_CHARGE_BLOCKING_MESSAGE } from '../utils/rechargeErrors';
import { addonUnitPrice } from '../utils/pricing';
import { readBoxDraft, clearBoxDraft, hashBundleItems } from '../utils/boxDraft';

// Draft-restore toast shown at most once per subscription per page load —
// initialize() re-runs on refetches and must not re-announce the same restore.
const draftRestoreAnnounced = new Set();

// True when two Recharge date strings fall on the same calendar day. Add-on
// (onetime) rows carry their own next_charge_scheduled_at — the save loop uses
// this to detect rows left behind on an old charge date after a reschedule and
// pull them onto the box's current date.
const sameChargeDay = (a, b) => String(a || '').slice(0, 10) === String(b || '').slice(0, 10);

// Utility function to extract servings from product description
const extractServings = (bodyHtml) => {
  if (!bodyHtml) return null;
  // Try to match the servings input pattern
  const servingsMatch = bodyHtml.match(/id="servings"[^>]*value="(\d+)"/i);
  if (servingsMatch && servingsMatch[1]) {
    return parseInt(servingsMatch[1], 10);
  }
  
  // Alternative pattern that might be used
  const altServingsMatch = bodyHtml.match(/name="servings"[^>]*value="(\d+)"/i);
  if (altServingsMatch && altServingsMatch[1]) {
    return parseInt(altServingsMatch[1], 10);
  }
  
  // Try a more general pattern that looks for any input with value and name/id containing "servings"
  const generalServingsMatch = bodyHtml.match(/input[^>]*value="(\d+)"[^>]*(?:name|id)="servings"/i);
  if (generalServingsMatch && generalServingsMatch[1]) {
    return parseInt(generalServingsMatch[1], 10);
  }
  
  // Try the reverse order (name/id first, then value)
  const reverseServingsMatch = bodyHtml.match(/input[^>]*(?:name|id)="servings"[^>]*value="(\d+)"/i);
  if (reverseServingsMatch && reverseServingsMatch[1]) {
    return parseInt(reverseServingsMatch[1], 10);
  }
  
  return null;
};

// Create a store with Zustand
const useBoxSelectionStore = create((set, get) => ({
  // State
  selectedProducts: [],
  bundleItems: [],
  onetimes: [],
  freeProduct: null,
  quantities: {},
  initialQuantities: {}, // Store initial quantities for comparison
  removedProductIds: [],
  // Set when the member confirms "Clear box" this session. It is the only
  // flow that legitimately queues many onetime deletions at once, so the
  // save-time mass-deletion circuit breaker allows bulk deletes only when
  // this is set. Reset after a successful save and on initialize.
  clearedBoxThisSession: false,
  products: [],
  totalServings: 0,
  totalItems: 0,
  isConfirmed: false,
  hasChanges: false, // Track if there are changes
  // Plan slot capacity, published by BoxBuilder once its bundle config
  // resolves (null until then). Lets components outside the builder tree —
  // e.g. the mobile charge-date card's box-status band — read fill state
  // from the same source the floating pill uses, without prop-drilling.
  planItemLimit: null,
  // Plan size parsed from the subscription's variant title ("6 items / …")
  // at hydration. Backs the plan-slot cap whenever `planItemLimit` is null
  // (BoxBuilder unmounted, config not resolved yet) so the cap can't
  // silently switch off mid-session.
  planItemLimitFallback: null,
  // The box size the member picked in the builder this session when it
  // differs from the size saved on the subscription:
  //   { sizeTitle, itemsCount, externalVariantId }
  // BoxBuilder's size selection is component state and re-syncs to the SAVED
  // variant whenever Overview refetches (reschedule, frequency change…) or
  // the builder remounts — while the rows picked for the bigger size live on
  // here. Keeping the intent in the store lets the builder recognise "this
  // box is over the limit because the member was switching to a bigger box"
  // and offer to keep going instead of telling them to remove items. Cleared
  // once a save commits, on cancel, and by a full re-hydration.
  pendingPlanSize: null,
  // One-shot (per page load) flag for addProduct's "plan add before
  // hydration" diagnostic — see the comment at that log call.
  preHydrationPlanAddLogged: false,
  lastClickedProduct: null, // Track the last clicked product
  subscription: null, // Store subscription object
  isSaving: false, // Track if selection is being saved
  // Set when a save attempt fails so the UI can ask the member to retry.
  // Cleared when a new save starts or when the member edits their box again
  // (both re-arm the normal auto-save path). While set, auto-save is paused —
  // otherwise a persistent failure would retry + toast in an endless loop.
  saveError: false,
  // True when initialize() couldn't match every saved bundle item / onetime to
  // a product in the loaded catalog. While set, saving is blocked: a save from
  // a partially-hydrated store would silently drop (and delete!) items the
  // member never touched. Cleared by the next initialize() with complete data.
  hydrationIncomplete: false,
  justSaved: false, // Track if we just saved to prevent re-init with stale data
  // Fresh { subscription, onetimes, savedAt } from the save's own internal
  // refetch. confirmSelection already re-reads the saved subscription and the
  // full onetimes list to rebuild its baselines; stashing them here lets the
  // post-save UI refresh reuse those responses instead of re-requesting the
  // same data (it then only needs to fetch charges). Consumed (and cleared)
  // by Overview.refreshAfterBoxSave.
  lastSaveRefresh: null,
  // Per-add-on click-time sync indicator: { [productId]: 'saving' | 'saved' }.
  // Driven by the debounced upsert lifecycle; 'saved' entries self-clear after
  // a short beat. Rendered as a small badge on the Your Box add-on rows.
  addonSyncStatus: {},
  saveTimeoutId: null, // Store timeout ID for failsafe
  klaviyoEventTimeoutId: null, // Store timeout ID for debounced Klaviyo event
  lastKlaviyoEventData: null, // Store last event data to prevent duplicates

  // Initialize the store with data from subscription
  initialize: (subscription, onetimes, products) => {
    const state = get();
    
    // Skip re-initialization if we are currently saving or just saved (within last 5 seconds)
    // This prevents overwriting our correct state with stale API data or during active editing
    if (state.isSaving || state.justSaved) {
      // Reset the flag after 5 seconds for justSaved
      if (state.justSaved) {
        setTimeout(() => {
          set({ justSaved: false });
        }, 5000);
      }
      return;
    }
    
    // For gift redemption (subscription.id === 'gift-redemption'), skip re-initialization
    // if we already have selected products to prevent losing user selections
    const isGiftRedemption = subscription?.id === 'gift-redemption';
    if (isGiftRedemption && state.selectedProducts.length > 0) {
      console.log('BoxSelectionService: Skipping re-init for gift redemption (already has selections)');
      return;
    }

    // Unsaved local edits win over incoming server data for the SAME
    // subscription: props refresh asynchronously (and can be stale), so
    // re-initializing here would clobber in-progress changes and pending
    // removals mid-edit. The next save persists the member's state; a
    // different subscription id is a genuine context switch and always
    // hydrates.
    if (state.hasChanges && String(state.subscription?.id || '') === String(subscription?.id || '')) {
      console.log('BoxSelectionService: Skipping re-init — unsaved local edits take precedence.');
      // Local edits only cover box CONTENTS — schedule fields are server-owned
      // and must still land here. Without this, a reschedule done while edits
      // are pending leaves the store on the old date, and the next save stamps
      // every add-on with it — re-creating a charge on the date the member
      // just moved away from ("my date switched back when I confirmed my box").
      const freshChargeDate = subscription?.next_charge_scheduled_at;
      if (freshChargeDate && state.subscription && freshChargeDate !== state.subscription.next_charge_scheduled_at) {
        set({ subscription: { ...state.subscription, next_charge_scheduled_at: freshChargeDate } });
      }
      return;
    }

    // Never resurrect rows this session already deleted: an onetimes list
    // fetched before the deletion still contains them, and hydrating from it
    // puts the removed add-on straight back in the drawer.
    onetimes = (onetimes || []).filter(ot => !isTombstonedOnetime(ot.id));
    
    // If the bundle selections are marked as fallback, we don't want to pre-populate
    // the store with those default items. Treat it as an empty plan so the
    // StatusBar (and the rest of the UI) starts with a clean slate.
    const isFallback = subscription?.include?.bundle_selections?.is_fallback;

    const bundleItems = isFallback
      ? []
      : (subscription?.include?.bundle_selections?.items || []);
    const freeProduct = subscription?.freeItemProduct || null;
    
    // Initialize quantities from bundle items and onetimes
    const quantities = {};
    
    // Add bundle items to quantities
    bundleItems.forEach(item => {
      quantities[item.external_product_id] = item.quantity;
    });

    // Add free product to quantities if it exists
    if (freeProduct) {
      quantities[freeProduct.id] = 1;
    }

    // Calculate initial selected products
    const selectedProducts = [];

    // Add bundle items to selected products
    bundleItems.forEach(item => {
      const product = products.find(p => p.id === item.external_product_id);
      if (product) {
        selectedProducts.push({
          ...product,
          quantity: item.quantity,
          isAddon: false,
          collection_id: item.collection_id || ""
        });
      }
    });

    // Add onetimes to selected products
    // We add uniqueId to handle multiple instances of same product with different prices
    //
    // Duplicate Recharge rows for the same product + bonus-ness (a bug state
    // our own save/upsert races can leave behind) are merged into ONE drawer
    // instance with the summed quantity — the whole save machinery matches
    // rows by product + bonus-ness, so two rows can never be edited
    // independently anyway; rendering them separately just shows the product
    // twice. The extra rows are healed (deleted, survivor quantity fixed) in
    // the background after hydration.
    const addonInstancesByKey = new Map();
    const duplicateOnetimeGroups = [];
    onetimes.forEach((onetime, index) => {
      const product = products.find(p => p.id === onetime.external_product_id.ecommerce);
      if (product) {
        // Generate unique ID for tracking separate instances of same product with different prices
        const uniqueId = `${product.id}_addon_init_${index}`;
        // Hydrate `isTierBonus` from the `_tier_bonus` property AND a $0
        // price. We require both because the earlier (buggy) version of the
        // save path stamped the property but let the variant price through —
        // those rows are NOT actually free and we shouldn't display them as
        // free in the cart. A stale $X row gets treated as a normal paid
        // addon; the next save flow will delete + recreate it at $0.
        const props = Array.isArray(onetime.properties) ? onetime.properties : [];
        const tierBonusProp = props.find(p => p?.name === '_tier_bonus');
        const priceNum = parseFloat(onetime.price || 0);
        const isTierBonus = Boolean(tierBonusProp) && priceNum === 0;

        const dupKey = `${product.id}_${isTierBonus ? 'bonus' : 'paid'}`;
        const existingEntry = addonInstancesByKey.get(dupKey);
        if (existingEntry) {
          existingEntry.instance.quantity += onetime.quantity;
          quantities[existingEntry.instance.uniqueId] = existingEntry.instance.quantity;
          existingEntry.group.extras.push(onetime);
          if (existingEntry.group.extras.length === 1) {
            duplicateOnetimeGroups.push(existingEntry.group);
          }
          return;
        }

        const instance = {
          ...product,
          quantity: onetime.quantity,
          isAddon: true,
          price: isTierBonus ? 0 : onetime.price,
          isTierBonus,
          tierId: isTierBonus ? (tierBonusProp?.value || '') : '',
          uniqueId: uniqueId
        };
        addonInstancesByKey.set(dupKey, {
          instance,
          group: { survivor: onetime, extras: [] }
        });
        selectedProducts.push(instance);
        quantities[uniqueId] = onetime.quantity;
      }
    });

    // Store initial quantities for comparison
    const initialQuantities = { ...quantities };
    
    // Add free product to selected products if it exists
    if (freeProduct) {
      selectedProducts.push({
        ...freeProduct,
        quantity: 1,
        isFree: true
      });
    }

    // ---- Partial-box draft restore ----
    // Plan picks only reach Recharge when the box is FULL (no Save button
    // before that), so navigating away mid-fill used to silently discard
    // progress. BoxBuilder stashes partial picks locally (utils/boxDraft);
    // merge them back whenever we hydrate the same server state they were
    // drafted against — any real save changes the hash and invalidates the
    // draft. Living here (not in a component effect) means later re-inits
    // from refetched data re-apply the draft instead of wiping it.
    let draftMergedCount = 0;
    if (!isGiftRedemption && subscription?.id) {
      const draft = readBoxDraft(subscription.id);
      const serverHash = hashBundleItems(bundleItems);
      if (draft && draft.baseHash === serverHash) {
        const planLimit = derivePlanItemLimit(subscription);
        const planCount = () => selectedProducts
          .filter((p) => !p.isAddon && !p.isFree)
          .reduce((sum, p) => sum + (p.quantity || 0), 0);
        if (planLimit && planCount() < planLimit) {
          draft.items.forEach((item) => {
            const product = products.find((p) => String(p.id) === String(item.id));
            if (!product) return;
            const existing = selectedProducts.find(
              (p) => !p.isAddon && !p.isFree && String(p.id) === String(item.id)
            );
            const missing = Math.min(
              (item.quantity || 0) - (existing?.quantity || 0),
              planLimit - planCount()
            );
            if (missing <= 0) return;
            if (existing) {
              existing.quantity += missing;
              quantities[product.id] = existing.quantity;
            } else {
              selectedProducts.push({ ...product, quantity: missing, isAddon: false, collection_id: "" });
              quantities[product.id] = missing;
            }
            draftMergedCount += missing;
          });
        }
      } else if (draft) {
        // Stale draft (server bundle changed since it was written) — discard.
        clearBoxDraft(subscription.id);
      }
    }
    
    // Detect incomplete hydration: every saved bundle item and onetime must
    // have a matching product in the loaded catalog, otherwise it silently
    // disappears from selectedProducts. Saving in that state would rewrite the
    // bundle without those items and delete their onetimes — real data loss
    // that the member never asked for. Saves are blocked while this is true.
    const missingBundleItems = bundleItems.filter(
      item => !products.find(p => p.id === item.external_product_id)
    );
    const missingOnetimes = onetimes.filter(
      ot => !products.find(p => p.id === ot.external_product_id?.ecommerce)
    );
    const hydrationIncomplete = missingBundleItems.length > 0 || missingOnetimes.length > 0;
    if (hydrationIncomplete) {
      console.warn('BoxSelectionService: hydration incomplete — saves blocked.', {
        missingBundleItems: missingBundleItems.map(i => i.external_product_id),
        missingOnetimes: missingOnetimes.map(o => o.external_product_id?.ecommerce),
        productsLoaded: products.length,
      });
    }
    
    // Calculate totals
    const { totalItems, totalServings } = calculateTotals(selectedProducts);
    
    set({
      selectedProducts,
      bundleItems,
      onetimes,
      freeProduct,
      quantities,
      initialQuantities, // Store initial quantities
      products,
      totalItems,
      totalServings,
      removedProductIds: [],
      clearedBoxThisSession: false,
      isConfirmed: false,
      // Draft-restored picks count as unsaved local edits: the member still
      // has to save them, and the hasChanges guard above keeps later re-inits
      // from clobbering them.
      hasChanges: draftMergedCount > 0,
      lastClickedProduct: null, // Reset lastClickedProduct
      subscription, // Store the subscription object
      planItemLimitFallback: derivePlanItemLimit(subscription),
      pendingPlanSize: null,
      isSaving: false, // Reset loading state
      saveError: false,
      addonSyncStatus: {},
      hydrationIncomplete
    });

    // Heal duplicate Recharge rows in the background so the server converges
    // on what the drawer now shows (one row, summed quantity). Skipped while
    // hydration is incomplete — healing needs a trustworthy picture.
    if (duplicateOnetimeGroups.length > 0 && !hydrationIncomplete && !isGiftRedemption) {
      healDuplicateOnetimeRows(duplicateOnetimeGroups);
    }

    if (draftMergedCount > 0 && !draftRestoreAnnounced.has(String(subscription.id))) {
      draftRestoreAnnounced.add(String(subscription.id));
      notification.success('We restored the box picks you made earlier.');
    }
  },
  
  // Set last clicked product
  setLastClickedProduct: (product) => {
    set({ lastClickedProduct: product });
  },

  // Publish the plan slot capacity (see planItemLimit above)
  setPlanItemLimit: (planItemLimit) => {
    set({ planItemLimit });
  },

  // Record (or clear, with null) the size the member is switching to — see
  // pendingPlanSize above.
  setPendingPlanSize: (pendingPlanSize) => {
    set({ pendingPlanSize: pendingPlanSize || null });
  },
  
  // Add a product to the box
  addProduct: (product) => {
    const { selectedProducts, quantities, products } = get();

    // If this is a free product, use the special handler
    if (product.isFree) {
      return get().addFreeProduct(product);
    }

    // Normalize product ID to ensure consistent comparison
    const productId = String(product.id);

    // Check if this product is an addon
    const isAddon = product.isAddon;

    // Plan-slot cap (see planSlotOverflow). BoxBuilder converts a plan add on
    // a full box to an add-on before calling here, but its isBoxFull is from
    // the last render, so two quick taps could still land a 13th item in a
    // 12-slot box. Same conversion, checked against live store state.
    if (!isAddon) {
      const overflow = planSlotOverflow(get(), 1);
      if (overflow) {
        addOverflowAsAddon(product, overflow.limit, 1);
        return;
      }
      // planSlotOverflow can only cap when a limit is derivable. With no
      // subscription in the store yet (an add racing initialize) there is
      // nothing to check against — log the first one per session so overfull
      // boxes built through this window stop being a mystery (pairs with the
      // save guard's planRows log).
      if (!get().subscription && !get().preHydrationPlanAddLogged) {
        set({ preHydrationPlanAddLogged: true });
        logPortalError('Plan add before hydration - cap unarmed', new Error('Cap unarmed'), {
          productId: String(product.id),
          planTotal: planItemTotal(get().selectedProducts) + 1,
        }).catch(() => {});
      }
    }

    // For addons, check if we already have this exact addon (same ID and price)
    let existingProductIndex = -1;
    if (isAddon) {
      existingProductIndex = selectedProducts.findIndex(p =>
        String(p.id) === productId &&
        p.isAddon &&
        p.price === product.price
      );
    } else {
      // For plan items, check by ID and exclude free items so adding the same
      // product doesn't accidentally increment the free item's quantity
      existingProductIndex = selectedProducts.findIndex(p =>
        String(p.id) === productId && !p.isAddon && !p.isFree
      );
    }

    // Create normalized product
    let normalizedProduct = { ...product, id: productId };

    // Handle different image structures
    if (!product.images?.edges) {
      normalizedProduct.images = {
        edges: [{
          node: {
            url: Array.isArray(product.images) ? product.images[0] : product.featured_image
          }
        }]
      };
    }

    // Handle different variant structures
    normalizedProduct.variants = Array.isArray(product.variants) ? product.variants : [];

    // Ensure descriptionHtml exists for servings calculation
    normalizedProduct.descriptionHtml = product.descriptionHtml || product.description || product.content || '';

    // Preserve collection_id if provided
    normalizedProduct.collection_id = product.collection_id || "";

    // Set as last clicked product
    set({ lastClickedProduct: normalizedProduct });

    // Add the product to the products array if it doesn't exist
    if (!products.some(p => String(p.id) === productId)) {
      set(state => ({
        products: [...state.products, normalizedProduct]
      }));
    }

    let addedAddon = null;

    if (existingProductIndex >= 0) {
      // Increment quantity of existing product
      const existingProduct = selectedProducts[existingProductIndex];
      const newQuantity = (existingProduct.quantity || 0) + 1;

      // Update quantities
      set(state => ({
        selectedProducts: state.selectedProducts.map((p, index) =>
          index === existingProductIndex ? { ...p, quantity: newQuantity } : p
        )
      }));
      addedAddon = existingProduct;
    } else {
      // Add new product with quantity 1
      // Generate unique ID for tracking in quantities map.
      // For plan items, use a '_plan' suffix when a free item with the same ID
      // already exists so the two entries don't collide in the quantities map
      // and findIndex can distinguish them via uniqueId.
      const hasFreeCollision = !isAddon && selectedProducts.some(p => p.isFree && String(p.id) === productId);
      const uniqueId = isAddon
        ? `${productId}_addon_${Date.now()}`
        : (hasFreeCollision ? `${productId}_plan` : productId);

      const newSelectedProduct = {
        ...normalizedProduct,
        quantity: 1,
        isAddon: isAddon,
        price: isAddon ? product.price : null,
        uniqueId: uniqueId // Add unique ID to track separate instances
      };
      set(state => ({
        quantities: { ...state.quantities, [uniqueId]: 1 },
        selectedProducts: [...state.selectedProducts, newSelectedProduct]
      }));
      addedAddon = newSelectedProduct;
    }

    // Persist paid add-ons at click time (debounced) so the eventual save
    // has nothing left to write for them. scheduleAddonUpsert ignores
    // non-addons, free items, and tier bonuses.
    if (addedAddon) {
      scheduleAddonUpsert(addedAddon);
    }

    // Force a state update to ensure UI reflects changes
    setTimeout(() => {
      // Recalculate totals and check for changes in one operation
      updateTotalsAndCheckChanges();
    }, 0);
  },
  
  // Add a free product to the box
  addFreeProduct: (product) => {
    const { selectedProducts, products } = get();
    
    // Normalize product ID to ensure consistent comparison
    const productId = String(product.id);
    
    // Create normalized product
    let normalizedProduct = { ...product, id: productId };
    
    // Handle different image structures
    if (!product.images?.edges) {
      normalizedProduct.images = {
        edges: [{
          node: {
            url: Array.isArray(product.images) ? product.images[0] : product.featured_image
          }
        }]
      };
    }
    
    // Handle different variant structures
    normalizedProduct.variants = Array.isArray(product.variants) ? product.variants : [];
    
    // Ensure descriptionHtml exists for servings calculation
    normalizedProduct.descriptionHtml = product.descriptionHtml || product.description || product.content || '';
    
    // Add the product to the products array if it doesn't exist
    if (!products.some(p => String(p.id) === productId)) {
      set(state => ({
        products: [...state.products, normalizedProduct]
      }));
    }
    
    // Remove any existing free products first
    const newSelectedProducts = selectedProducts.filter(p => !p.isFree);
    
    // Add the new free product with quantity 1
    set(state => ({
      quantities: { ...state.quantities, [productId]: 1 },
      selectedProducts: [...newSelectedProducts, { 
        ...normalizedProduct, 
        quantity: 1,
        isFree: true
      }]
    }));
    
    // Force a state update to ensure UI reflects changes
    setTimeout(() => {
      updateTotalsAndCheckChanges();
    }, 0);
  },

  // Remove the free item row(s). updateQuantity deliberately never matches
  // free rows (they can share a product id with a real plan row), so this is
  // the one way to take the free item out of the box.
  removeFreeProduct: () => {
    set(state => {
      const freeRows = state.selectedProducts.filter(p => p.isFree);
      if (!freeRows.length) return state;
      const newQuantities = { ...state.quantities };
      freeRows.forEach(row => {
        delete newQuantities[row.uniqueId || String(row.id)];
      });
      return {
        ...state,
        quantities: newQuantities,
        selectedProducts: state.selectedProducts.filter(p => !p.isFree),
      };
    });
    setTimeout(() => {
      updateTotalsAndCheckChanges();
    }, 0);
  },

  // Update product quantity
  // productId can be either the product ID or the uniqueId for addon instances
  updateQuantity: (productId, quantity, isAddon = false, price = null) => {
    // Ensure productId is a string for comparison
    const targetProductId = String(productId);

    // Handle removal (quantity === 0)
    if (quantity === 0) {
      let removedAddon = null;
      set(state => {
        // Find the index of the specific product instance
        // For addons with price specified, match by ID, isAddon, and price
        // Otherwise match by ID and isAddon
        const productIndex = state.selectedProducts.findIndex(p => {
          if (p.uniqueId === targetProductId) return true; // Match by uniqueId if provided
          // Free rows are only addressable via removeFreeProduct/addFreeProduct.
          // When a plan row shares the free item's product id (uniqueId
          // `<id>_plan`), a raw-id match used to hit whichever row came first —
          // clearing by id could remove the FREE row and strand the plan row,
          // which then overflowed the plan on every save ("Plan overflow" logs).
          if (p.isFree) return false;
          if (String(p.id) !== targetProductId) return false;
          if (!!p.isAddon !== !!isAddon) return false;
          if (isAddon && price !== null && p.price !== price) return false;
          return true;
        });

        if (productIndex === -1) {
          console.warn(`Product not found for removal: ID=${targetProductId}, isAddon=${isAddon}, price=${price}`);
          return state; // No matching product found
        }

        const productToRemove = state.selectedProducts[productIndex];
        if (productToRemove.isAddon && !productToRemove.isFree) {
          removedAddon = productToRemove;
        }

        // Create a new array without this specific product
        const newSelectedProducts = [
          ...state.selectedProducts.slice(0, productIndex),
          ...state.selectedProducts.slice(productIndex + 1)
        ];

        // Determine if the removed product was originally part of the subscription.
        // NOTE: match on the real product id, not targetProductId — addon rows are
        // removed by uniqueId (`<id>_addon_init_<n>`), which never matches bundle
        // items or onetimes, so removals were silently not being tracked.
        const realProductId = String(productToRemove.id);
        const wasInitial = Object.keys(state.initialQuantities).some(() => {
           const initialBundleItem = state.bundleItems.find(item => String(item.external_product_id) === realProductId);
           const initialOnetime = state.onetimes.find(ot => String(ot.external_product_id.ecommerce) === realProductId || String(ot.id) === targetProductId);

           if (!isAddon && initialBundleItem && initialBundleItem.quantity > 0) return true;
           if (isAddon && initialOnetime && initialOnetime.quantity > 0) return true;
           return false;
        });

        // Add to removedProductIds only if it was part of the initial subscription
        const newRemovedProductIds = wasInitial
            ? [...state.removedProductIds, realProductId]
            : state.removedProductIds;

        // Update quantities map
        const newQuantities = { ...state.quantities };
        if (productToRemove.uniqueId) {
          delete newQuantities[productToRemove.uniqueId];
        } else {
          delete newQuantities[targetProductId];
        }

        return {
          ...state,
          quantities: newQuantities,
          selectedProducts: newSelectedProducts,
          removedProductIds: newRemovedProductIds,
        };
      });
      // Persist the removal right away. Auto-saves are additive-only (they
      // never delete onetimes), so without this an add-on removal would only
      // persist on an explicit manual save. Fire-and-forget: failures keep
      // the row in state + the id in removedProductIds so the manual-save
      // backstop can retry.
      if (removedAddon) {
        deleteOnetimeRowsForRemovedAddon(removedAddon);
      }
    } else {
      // Updating quantity (not removing)
      let updatedAddon = null;

      // Plan-slot cap (see planSlotOverflow). Every "+" stepper — old-design
      // card grids, new-design browse cards, the desktop sidebar — lands
      // here, and none of them checked the plan limit; only a product's
      // first add went through BoxBuilder's isBoxFull conversion. The extra
      // unit used to reach Recharge as a 422 on every save attempt. Fill
      // whatever room is left, and convert the rest to a paid add-on.
      let quantityToApply = quantity;
      let overflowUnits = 0;
      let overflowRow = null;
      let overflowLimit = null;
      const planRow = findPlanRow(get().selectedProducts, targetProductId, isAddon, price);
      if (planRow) {
        const current = Number(planRow.quantity) || 0;
        const overflow = planSlotOverflow(get(), quantity - current);
        if (overflow) {
          const room = Math.max(0, overflow.limit - planItemTotal(get().selectedProducts));
          quantityToApply = current + room;
          overflowUnits = quantity - current - room;
          overflowRow = planRow;
          overflowLimit = overflow.limit;
        }
      }

      set(state => {
        // Find the index of the specific product instance
        const productIndex = state.selectedProducts.findIndex(p => {
          if (p.uniqueId === targetProductId) return true; // Match by uniqueId if provided
          // Free rows are only addressable via removeFreeProduct/addFreeProduct
          // (see the removal matcher above).
          if (p.isFree) return false;
          if (String(p.id) !== targetProductId) return false;
          if (!!p.isAddon !== !!isAddon) return false;
          if (isAddon && price !== null && p.price !== price) return false;
          return true;
        });

        if (productIndex === -1) {
           console.warn(`Product not found for update: ID=${targetProductId}, isAddon=${isAddon}, price=${price}`);
           return state;
        }

        const productToUpdate = state.selectedProducts[productIndex];
        if (productToUpdate.isAddon && !productToUpdate.isFree) {
          updatedAddon = productToUpdate;
        }

        // Create a new array with the updated product
        const newSelectedProducts = [...state.selectedProducts];
        newSelectedProducts[productIndex] = {
          ...newSelectedProducts[productIndex],
          quantity: quantityToApply
        };

        // Update quantities map using uniqueId if available
        const quantityKey = productToUpdate.uniqueId || targetProductId;
        const newQuantities = { ...state.quantities, [quantityKey]: quantityToApply };

        return {
          ...state,
          quantities: newQuantities,
          selectedProducts: newSelectedProducts,
          // Remove from removedProductIds if it was previously removed and is now added back
          removedProductIds: state.removedProductIds.filter(id => id !== targetProductId),
        };
      });
      // Persist the new quantity at click time (debounced). Tier bonuses and
      // free items are filtered out inside scheduleAddonUpsert.
      if (updatedAddon) {
        scheduleAddonUpsert(updatedAddon);
      }
      if (overflowUnits > 0) {
        addOverflowAsAddon(overflowRow, overflowLimit, overflowUnits);
      }
    }

    // Recalculate totals and check for changes after state update
    // Use setTimeout to ensure state update is processed before recalculation
    setTimeout(() => {
        updateTotalsAndCheckChanges();
    }, 0);
  },
  
  // Confirm selection.
  // `options.isAutoSave` marks saves triggered by the 2s auto-save timer (as
  // opposed to an explicit Save click). Auto-saves are additive-only: they
  // never delete onetimes, so a transient bad state can't destroy add-ons.
  confirmSelection: async (collectionProductMap = {}, rechargeBundleVariantId, bundleVariantId, newPlanItemLimit = null, options = {}) => {
    const { isAutoSave = false, planCollectionsResolved = false } = options;
    // Quick upfront check: ensure we still have a *valid-looking* Recharge session.
    try {
      const stored = localStorage.getItem('rechargeSession');
      if (!stored) {
        window.dispatchEvent(new Event('session-expired'));
        notification.error('Your session expired. Please log in again.');
        return;
      }
      const parsed = JSON.parse(stored);
      if (!parsed || !parsed.apiToken) {
        window.dispatchEvent(new Event('session-expired'));
        notification.error('Your session expired. Please log in again.');
        return;
      }
    } catch {
      // If parsing failed treat as invalid session
      window.dispatchEvent(new Event('session-expired'));
      notification.error('Your session expired. Please log in again.');
      return;
    }

    // Settle click-time add-on writes before diffing: flush debounced ones
    // immediately (so the save loop can no-op them) and wait for anything
    // already in flight. Cancelling used to force the sequential save loop
    // to re-create those rows and pay pacing delays for each one.
    const flushedCount = await flushPendingAddonUpserts();

    const { 
      selectedProducts, 
      onetimes, 
      removedProductIds,
      quantities,
      subscription,
      hydrationIncomplete,
      clearedBoxThisSession
    } = get();
    
    if (!selectedProducts.length) {
      notification.error('No products selected');
      return;
    }
    
    // Refuse to save from a partially-hydrated store: items we failed to match
    // to the catalog are absent from selectedProducts, so persisting this state
    // would drop them from the bundle and delete their onetimes.
    if (hydrationIncomplete) {
      console.error('Box save blocked: store hydration incomplete (some saved items missing from catalog).');
      logPortalError('Box save blocked - incomplete hydration', new Error('Hydration incomplete'), {
        subscriptionId: subscription?.id,
        isAutoSave,
        selectedProductsCount: selectedProducts.length,
        onetimesCount: onetimes.length,
      }).catch(logErr => {
        console.error('Failed to send error log:', logErr);
      });
      if (!isAutoSave) {
        notification.loadError("We couldn't load all of your box items.");
      }
      // Pause auto-save (saveError) so a broken state doesn't retry in a loop.
      set({ saveError: true });
      return null;
    }
    
    try {
      set({ isSaving: true, saveError: false });
      
      // Failsafe: reset isSaving after 30 seconds to prevent getting stuck
      const timeoutId = setTimeout(() => {
        console.warn('Box selection save operation timed out, resetting isSaving state');
        set({ isSaving: false });
      }, 30000);
      
      // Store timeout ID to clear it on success
      set({ saveTimeoutId: timeoutId });
      
      // Check if subscription data is available
      if (!subscription || !subscription.include || !subscription.include.bundle_selections) {
        notification.error('Membership data is missing');
        return;
      }
      
      const bundleSelectionId = subscription.include.bundle_selections.id;
      const addressId = subscription.include.address.id;
      let nextChargeDate = subscription.next_charge_scheduled_at;
      
      // Check if the plan has changed by comparing Shopify variant IDs.
      // NOTE: this must compare like-for-like. `bundle_selections.external_variant_id`
      // is a SHOPIFY variant id, so it's compared against `bundleVariantId`
      // (also Shopify). The old code compared it against the Recharge bundle
      // variant id (`rechargeBundleVariantId`), which NEVER matches — so every
      // save took the heavyweight PUT /bundles plan-change path.
      const hasPlanChanged = Boolean(bundleVariantId) &&
        String(subscription.include.bundle_selections.external_variant_id) !== String(bundleVariantId);

      // Plan-slot limit for the overflow guard further down (right before the
      // bundle write decision). Plan changes keep their own exact-count
      // validation below.
      const planLimitForSave = hasPlanChanged ? null : effectivePlanItemLimit(get());
      const planTotalForSave = planItemTotal(selectedProducts);

      // 1. Update bundle selection items (regular products, not addons)
      let bundleSelectionItems = selectedProducts
        .filter(p => !p.isAddon && !p.isFree)
        .map(product => {
          const productId = String(product.id);
          const mappedCollectionId = collectionProductMap[productId];
          
          const originalBundleItem = subscription.include.bundle_selections.items.find(
            item => String(item.external_product_id) === productId
          );
          
          // When plan has changed, prefer the mapped collection from the new plan
          // Don't use originalBundleItem collection as it may be from the old plan
          const collectionId = hasPlanChanged
            ? (mappedCollectionId || product.collection_id || "")
            : (mappedCollectionId || product.collection_id || originalBundleItem?.collection_id || "");
          
          return {
            collection_id: String(collectionId),
            collection_source: 'shopify',
            external_product_id: String(product.id),
            external_variant_id: product.variants && product.variants.length > 0 ? String(product.variants[0].id) : "",
            quantity: product.quantity,
            _productTitle: product.title // Keep title for error messaging (will be removed before API call)
          };
        });
      
      // Pre-send guard (Oct 2026): a plan row whose product sits in NONE of
      // the plan's collections can never be saved — Recharge validates
      // membership and 422s the whole bundle ("wasn't found in its
      // correspondent collection"). Production showed a member re-failing
      // every auto-save over a checkout add-on (Breakfast Tea) that a stale
      // Recharge row had filed under Pastries. Decided only when the caller
      // confirms every plan collection was resolved from the bundle feed, so
      // a partial feed can never eject a legitimate item. The row is dropped
      // and the member asked to refill the slot, instead of sending a write
      // that is known to fail.
      if (planCollectionsResolved === true) {
        const notInPlan = bundleSelectionItems.filter(item => !collectionProductMap[item.external_product_id]);
        if (notInPlan.length > 0) {
          const dropped = dropPlanItemsNotInPlan(notInPlan.map(i => i.external_product_id));
          logPortalError('Box item not in plan collections - removed', new Error('Item not in plan collections'), {
            subscriptionId: subscription.id,
            customerEmail: subscription?.include?.customer?.email,
            source: 'pre-send',
            hasPlanChanged,
            isAutoSave,
            rejectedItems: notInPlan.map(i => ({ productId: i.external_product_id, collectionId: i.collection_id, title: i._productTitle || null })),
            removedFromBox: dropped.removed,
          }).catch(logErr => {
            console.error('Failed to send error log:', logErr);
          });
          notification.error(dropped.message, { duration: 12000 });
          set({ isSaving: false, saveTimeoutId: null, saveError: true });
          return null;
        }
      }

      // Validate that all products have valid collection mappings
      const productsWithoutCollection = bundleSelectionItems.filter(item => !item.collection_id);
      if (productsWithoutCollection.length > 0) {
        const productNames = productsWithoutCollection.map(p => p._productTitle || p.external_product_id).join(', ');
        console.error(`Products without valid collection mapping: ${productNames}`);
        
        // Log error to backend for debugging
        logPortalError('Products without collection mapping', new Error('Missing collection IDs'), {
          productsWithoutCollection: productsWithoutCollection.map(p => ({
            id: p.external_product_id,
            title: p._productTitle
          })),
          hasPlanChanged,
          collectionProductMapKeys: Object.keys(collectionProductMap),
          subscriptionId: subscription.id
        }).catch(logErr => {
          console.error('Failed to send error log:', logErr);
        });
        
        notification.error(
          `Some items couldn't be saved because they don't belong to your current plan. Please remove "${productsWithoutCollection[0]._productTitle || 'the incompatible item'}" and try again.`,
          10000
        );
        
        set({ isSaving: false, saveTimeoutId: null, saveError: true });
        return null;
      }
      
      // Remove the temporary _productTitle before sending to API
      bundleSelectionItems = bundleSelectionItems.map(({ _productTitle, ...item }) => item);      
      const purchaseItemId = subscription.include.bundle_selections.purchase_item_id;
      
      // Validate item count when plan is changing
      if (hasPlanChanged && newPlanItemLimit) {
        // Calculate total quantity of all items (sum of all quantities)
        const totalItemQuantity = bundleSelectionItems.reduce((sum, item) => sum + (item.quantity || 0), 0);
        
        // Check if total quantity matches the new plan's item limit
        if (totalItemQuantity !== newPlanItemLimit) {
          console.error(`Plan change validation failed: Selected ${totalItemQuantity} items but new plan requires ${newPlanItemLimit} items`);
          
          // Show error notification with specific guidance
          notification.error(
            `Plan change failed. The page will refresh so you can start fresh.`,
            10000 // Show for 10 seconds
          );
          
          // Reset loading state
          set({ isSaving: false, saveTimeoutId: null });
          
          // Refresh the page after a short delay to let the user read the message
          setTimeout(() => {
            window.location.reload();
          }, 3000);
          
          return null;
        }
      }
      
      // Skip the bundle write entirely when nothing about the plan items
      // changed — addon-only edits used to rewrite an identical item list on
      // every save. Never skip on a plan change or while the saved selection
      // is a fallback (fallbacks must be replaced by a real member selection).
      const savedItems = subscription.include.bundle_selections.items || [];
      const bundleItemKey = (item) => [
        String(item.external_product_id),
        String(item.external_variant_id),
        String(item.collection_id),
        Number(item.quantity)
      ].join('|');
      const bundleItemsUnchanged =
        !hasPlanChanged &&
        !subscription.include.bundle_selections.is_fallback &&
        savedItems.length === bundleSelectionItems.length &&
        savedItems.map(bundleItemKey).sort().join(',') ===
          bundleSelectionItems.map(bundleItemKey).sort().join(',');

      // Refuse to send a bundle Recharge will reject anyway (422 on every
      // auto-save). The click-time cap should make this unreachable; when a
      // path slips past it the member gets the fix instead, and the rows are
      // logged so the path can be found. Scoped to saves that would actually
      // WRITE the bundle: boxes that arrive overfull from Recharge (variant
      // resized upstream without trimming the bundle) used to lock the member
      // out of EVERY save — production logs show members retrying add-on-only
      // edits against this wall. Those writes don't touch the bundle and are
      // safe to let through; plan-item writes stay blocked until the box fits.
      if (!bundleItemsUnchanged && planLimitForSave && planTotalForSave > planLimitForSave) {
        const excess = planTotalForSave - planLimitForSave;
        // The box fits the size the member was switching to: this isn't an
        // overfull box, it's a size switch the builder lost (its selection
        // re-synced to the saved variant on a refetch/remount). BoxBuilder
        // asks "keep going?" for this state — stay quiet here so the member
        // isn't told to remove items from a box they filled on purpose.
        const pendingPlanSize = get().pendingPlanSize;
        const fitsPendingSize = Boolean(pendingPlanSize) &&
          planTotalForSave <= Number(pendingPlanSize.itemsCount);
        console.error(`Box save blocked: ${planTotalForSave} plan items for a ${planLimitForSave}-item plan.`);
        logPortalError(
          fitsPendingSize ? 'Box save blocked - pending size switch' : 'Box save blocked - plan overflow',
          new Error('Plan overflow'),
          {
            subscriptionId: subscription?.id,
            isAutoSave,
            planItemLimit: get().planItemLimit,
            planItemLimitFallback: get().planItemLimitFallback,
            planTotal: planTotalForSave,
            pendingPlanSize,
            planRows: selectedProducts
              .filter(p => !p.isAddon && !p.isFree)
              .map(p => ({ id: String(p.id), title: p.title, quantity: p.quantity, uniqueId: p.uniqueId || null })),
            lastClickedProductId: get().lastClickedProduct?.id ? String(get().lastClickedProduct.id) : null,
          }
        ).catch(logErr => {
          console.error('Failed to send error log:', logErr);
        });
        // Auto-saves re-fire after every edit while the box is overfull, and
        // repeating the same red toast at a member who is already trying to
        // fix it reads as "the site is broken" (one member got 5 in 8 minutes
        // swapping items). Throttle the AUTOSAVE toast to one per 5 minutes
        // per subscription; an explicit Save click always gets feedback.
        let showOverflowToast = !fitsPendingSize;
        if (isAutoSave && showOverflowToast) {
          const toastKey = `wg-overflow-toast-${subscription?.id}`;
          try {
            const lastShown = Number(window.sessionStorage.getItem(toastKey)) || 0;
            showOverflowToast = Date.now() - lastShown > 5 * 60 * 1000;
            if (showOverflowToast) window.sessionStorage.setItem(toastKey, String(Date.now()));
          } catch { /* Private mode — keep the old always-toast behavior */ }
        }
        if (showOverflowToast) {
          notification.error(
            `Your box has ${planTotalForSave} items but your plan holds ${planLimitForSave}. ` +
              `Remove ${excess} ${excess === 1 ? 'item' : 'items'} (swapping won't free a slot), ` +
              `or add ${excess === 1 ? 'it' : 'them'} as add-ons.`,
            { duration: 10000 }
          );
        }
        clearTimeout(timeoutId);
        // saveError pauses auto-save until the member edits the box again.
        set({ isSaving: false, saveTimeoutId: null, saveError: true });
        return null;
      }
      
      // Update bundle selection if we have any items
      let bundleWrote = false;
      if (bundleSelectionItems.length > 0 && !bundleItemsUnchanged) {
        if (hasPlanChanged || !bundleSelectionId) {
          // Plan change → the bundle (product/variant) itself must be updated.
          // Null bundleSelectionId → the saved selection is a Recharge
          // FALLBACK (never explicitly saved), which has no row to PUT.
          // updateBundleSelectionItems(null) used to slip through the SDK's
          // id check and hit PUT /bundle_selections without an id — a 405
          // "method not allowed" every time. PUT /bundles/{purchase_item_id}
          // creates/replaces the selection, which is what both cases need.
          await updateBundle(purchaseItemId, {
            external_product_id: { ecommerce: subscription.include.bundle_selections.external_product_id },
            external_variant_id: { ecommerce: bundleVariantId || subscription.include.bundle_selections.external_variant_id },
            items: bundleSelectionItems
          });
        } else {
          // If plan hasn't changed, just update the items
          await updateBundleSelectionItems(bundleSelectionId, bundleSelectionItems);
        }
        bundleWrote = true;
      }
      
      // 2. Handle addons (onetimes) - Process one at a time
      const addonProducts = selectedProducts.filter(p => p.isAddon && !p.isFree);

      // The store's subscription can be stale here: initialize() skips
      // re-hydration while local edits are pending, so a reschedule done
      // mid-edit (or from another tab/device) may not be reflected in
      // `nextChargeDate`. Stamping add-ons with a stale date re-creates a
      // charge on the old date — the "I changed my date but it switched back
      // when I confirmed my box" bug. Fetch the live date and use it for
      // every onetime write below. Only needed when add-on writes can happen;
      // uncached read; on failure the stored date is kept so a transient read
      // error never blocks a save.
      if (addonProducts.length > 0) {
        try {
          const liveSubResponse = await fetchSubscription(subscription.id);
          const liveSub = liveSubResponse?.subscription || liveSubResponse;
          const liveChargeDate = liveSub?.next_charge_scheduled_at;
          if (liveChargeDate && liveChargeDate !== nextChargeDate) {
            console.warn(`Box save: stored charge date ${nextChargeDate} is stale — using live date ${liveChargeDate}.`);
            nextChargeDate = liveChargeDate;
            const storeSub = get().subscription;
            if (storeSub) {
              set({ subscription: { ...storeSub, next_charge_scheduled_at: liveChargeDate } });
            }
          }
        } catch (liveDateError) {
          console.error('Box save: failed to fetch live charge date, using stored date:', liveDateError);
          logPortalError('Box save - live charge date fetch failed', liveDateError, {
            subscriptionId: subscription?.id,
            isAutoSave,
            storedChargeDate: nextChargeDate,
          }).catch(logErr => {
            console.error('Failed to send error log:', logErr);
          });
        }
      }

      // Recharge allows multiple onetimes per (address, external_product_id)
      // pair, distinguished only by their own `id`. The portal can have the
      // same product in two roles at once: a paid addon (e.g. butter at $8)
      // AND a tier bonus (butter at $0). They're separate rows in Recharge.
      // We disambiguate by the presence of a `_tier_bonus` property — a
      // bonus row always has it, a paid row never does.
      const isBonusOnetime = (ot) => {
        const props = Array.isArray(ot?.properties) ? ot.properties : [];
        return props.some(p => p?.name === '_tier_bonus');
      };

      // Find the existing Recharge onetime that corresponds to this local
      // product instance — same Shopify product, same bonus-ness.
      const findExistingFor = (product) => onetimes.find(ot =>
        ot.external_product_id?.ecommerce === product.id &&
        isBonusOnetime(ot) === Boolean(product.isTierBonus)
      );

      // True when an existing bonus onetime is already correct ($0 + tagged
      // — Recharge stored what we wanted on a previous save).
      const isExistingZeroBonus = (onetime) => {
        if (!onetime) return false;
        const priceNum = parseFloat(onetime.price || 0);
        return priceNum === 0 && isBonusOnetime(onetime);
      };

      // Process addons with limited concurrency. `mutated` tracks whether this
      // addon actually needed an API write — the pacing delay only applies
      // after a real mutation. Unchanged add-ons (including ones just flushed
      // from click-time upserts) pay no sleep.
      const addonMutationFlags = await mapWithConcurrency(
        addonProducts,
        ONETIME_WRITE_CONCURRENCY,
        async (product) => {
          try {
            const existingOnetime = findExistingFor(product);
            let mutated = false;

            if (product.isTierBonus) {
              // Tier-bonus path: the row in Recharge MUST be at $0 with the
              // `_tier_bonus` property. createOnetimeItem proxies through
              // wildgrain-api's admin endpoint to actually land the $0 price
              // (the customer-portal token can't override `price`).
              if (existingOnetime && isExistingZeroBonus(existingOnetime)) {
                // A date mismatch means the row was left behind on an old
                // charge date (e.g. by a reschedule) — converge it too.
                if (
                  existingOnetime.quantity !== product.quantity ||
                  (nextChargeDate && !sameChargeDay(existingOnetime.next_charge_scheduled_at, nextChargeDate))
                ) {
                  try {
                    await updateOnetime(existingOnetime.id, {
                      quantity: product.quantity,
                      next_charge_scheduled_at: nextChargeDate
                    });
                    applyOnetimeQuantityInStore(existingOnetime.id, product.quantity, nextChargeDate);
                  } catch (updateError) {
                    // Stale bonus row: recreate through the $0 admin proxy
                    // rather than failing the whole save on a 404.
                    if (!isOnetimeGoneError(updateError)) throw updateError;
                    console.warn(`Box save: bonus onetime ${existingOnetime.id} no longer exists in Recharge; recreating.`);
                    dropGoneOnetimeFromStore(existingOnetime.id);
                    const onetimeProps = Array.isArray(product.properties) ? product.properties : [];
                    registerOnetimeRowInStore(await createOnetimeItem(
                      addressId,
                      {
                        id: product.id,
                        title: product.title,
                        quantity: product.quantity,
                        variants: product.variants
                      },
                      nextChargeDate,
                      [
                        ...onetimeProps,
                        { name: '_tier_bonus', value: String(product.tierId || 'unknown') },
                      ],
                      '0.00'
                    ));
                  }
                  mutated = true;
                }
              } else {
                if (existingOnetime) {
                  // Existing bonus row exists but isn't at $0 (legacy from
                  // before the admin proxy). Delete and recreate properly.
                  try {
                    await deleteOnetime(existingOnetime.id);
                    dropGoneOnetimeFromStore(existingOnetime.id);
                    await paceOnetimeMutation();
                  } catch (delErr) {
                    console.error(`Error deleting stale onetime before bonus recreate for ${product.id}:`, delErr);
                  }
                }
                const onetimeProps = Array.isArray(product.properties) ? product.properties : [];
                const combinedProps = [
                  ...onetimeProps,
                  { name: '_tier_bonus', value: String(product.tierId || 'unknown') },
                ];
                registerOnetimeRowInStore(await createOnetimeItem(
                  addressId,
                  {
                    id: product.id,
                    title: product.title,
                    quantity: product.quantity,
                    variants: product.variants
                  },
                  nextChargeDate,
                  combinedProps,
                  '0.00'
                ));
                mutated = true;
              }
            } else if (existingOnetime) {
              // Regular paid addon, already exists. Update when the quantity
              // changed OR the row is stranded on an old charge date.
              if (
                existingOnetime.quantity !== product.quantity ||
                (nextChargeDate && !sameChargeDay(existingOnetime.next_charge_scheduled_at, nextChargeDate))
              ) {
                try {
                  await updateOnetime(existingOnetime.id, {
                    quantity: product.quantity,
                    next_charge_scheduled_at: nextChargeDate
                  });
                  applyOnetimeQuantityInStore(existingOnetime.id, product.quantity, nextChargeDate);
                } catch (updateError) {
                  // Stale local row (Recharge dropped it server-side, e.g. by
                  // consolidating duplicates): recreate instead of failing the
                  // whole save on a 404.
                  if (!isOnetimeGoneError(updateError)) throw updateError;
                  console.warn(`Box save: onetime ${existingOnetime.id} no longer exists in Recharge; recreating.`);
                  dropGoneOnetimeFromStore(existingOnetime.id);
                  const onetimeProps = Array.isArray(product.properties) ? product.properties : [];
                  registerOnetimeRowInStore(await createOnetimeItem(
                    addressId,
                    {
                      id: product.id,
                      title: product.title,
                      quantity: product.quantity,
                      variants: product.variants
                    },
                    nextChargeDate,
                    onetimeProps,
                    null
                  ));
                }
                mutated = true;
              }
            } else {
              // New regular addon. Note: this path runs even if a tier-bonus
              // row exists for the same product, because findExistingFor()
              // disambiguates by bonus-ness.
              const onetimeProps = Array.isArray(product.properties) ? product.properties : [];
              registerOnetimeRowInStore(await createOnetimeItem(
                addressId,
                {
                  id: product.id,
                  title: product.title,
                  quantity: product.quantity,
                  variants: product.variants
                },
                nextChargeDate,
                onetimeProps,
                null
              ));
              mutated = true;
            }
            if (mutated) {
              await paceOnetimeMutation();
            }
            return mutated;
          } catch (error) {
            console.error(`Error processing addon ${product.id}:`, error);
            throw error;
          }
        }
      );
      const anyAddonMutated = addonMutationFlags.some(Boolean);

      // 3. Handle removed onetimes - Process one at a time.
      //
      // An existing onetime should be kept iff a selected addon product
      // matches BOTH its product id AND its bonus-ness. This is what makes
      // "remove the paid butter while keeping the bonus butter" work — they
      // share an external_product_id but are distinct rows.
      //
      // SAFETY (P0): absence from selectedProducts alone is NOT enough to
      // delete. A onetime is only deleted when the member explicitly removed
      // it this session (tracked in removedProductIds) — and never during an
      // auto-save. Anything else (hydration gaps, races, stale state) leaves
      // the row untouched; worst case is a harmless orphan the member can
      // still see and remove after a refresh.
      const wasExplicitlyRemoved = (ot) => {
        const onetimeProductId = String(ot.external_product_id?.ecommerce || '');
        return removedProductIds.some(removedId =>
          String(removedId) === onetimeProductId || String(removedId) === String(ot.id)
        );
      };
      let onetimesToDelete = isAutoSave ? [] : onetimes.filter(ot => {
        const onetimeProductId = String(ot.external_product_id?.ecommerce || '');
        const otIsBonus = isBonusOnetime(ot);
        const hasMatch = selectedProducts.some(p =>
          p.isAddon && !p.isFree &&
          String(p.id) === onetimeProductId &&
          Boolean(p.isTierBonus) === otIsBonus
        );
        if (hasMatch) return false;
        return wasExplicitlyRemoved(ot);
      });

      // Mass-deletion circuit breaker: the only flow that legitimately queues
      // many deletions at once is "Clear box". Anything else asking to delete
      // this many rows in one save is treated as a bug (stale/duplicated
      // removedProductIds, hydration race, hot-reload artifact...): keep the
      // rows, log loudly. Worst case is orphan add-ons the member can still
      // see and remove — never silent data loss.
      const MAX_ONETIME_DELETIONS_PER_SAVE = 3;
      if (onetimesToDelete.length > MAX_ONETIME_DELETIONS_PER_SAVE && !clearedBoxThisSession) {
        console.error('Box save: mass onetime deletion blocked by circuit breaker.', {
          attempted: onetimesToDelete.map(ot => ({ id: ot.id, title: ot.product_title })),
          removedProductIds,
        });
        logPortalError('Box save - mass onetime deletion blocked', new Error('Onetime deletion circuit breaker tripped'), {
          subscriptionId: subscription?.id,
          isAutoSave,
          attemptedDeleteCount: onetimesToDelete.length,
          attemptedDeleteIds: onetimesToDelete.map(ot => ot.id),
          attemptedDeleteProducts: onetimesToDelete.map(ot => ot.external_product_id?.ecommerce),
          removedProductIds,
        }).catch(logErr => {
          console.error('Failed to send error log:', logErr);
        });
        onetimesToDelete = [];
      }

      // Durable telemetry: save-time deletions are the path behind every
      // "my add-ons disappeared" report, so log them — but as ONE aggregated
      // entry per save, not one per row (keeps the backend log readable).
      if (onetimesToDelete.length > 0) {
        logPortalError('Box save - deleting onetimes', new Error('Onetimes deleted on save'), {
          subscriptionId: subscription?.id,
          isAutoSave,
          clearedBoxThisSession,
          removedProductIds,
          deleting: onetimesToDelete.map(ot => ({
            onetimeId: ot.id,
            productId: ot.external_product_id?.ecommerce,
            productTitle: ot.product_title,
          })),
        }).catch(() => {});
      }

      // Delete onetimes that are no longer selected (limited concurrency).
      const deleteFlags = await mapWithConcurrency(
        onetimesToDelete,
        ONETIME_WRITE_CONCURRENCY,
        async (onetimeToDelete) => {
          try {
            await deleteOnetime(onetimeToDelete.id);
            recordOnetimeTombstone(onetimeToDelete.id);
            await paceOnetimeMutation();
            return true;
          } catch (error) {
            console.error(`Error removing onetime ${onetimeToDelete.id}:`, error);
            // Don't throw if the onetime doesn't exist — just log and continue.
            // This can happen if the item was already removed.
            return false;
          }
        }
      );
      const deletedOnetimeCount = deleteFlags.filter(Boolean).length;

      // Re-read subscription/onetimes only when this save actually mutated
      // server state in the save loop. Click-time flushes already registered
      // their rows locally — no need to round-trip Recharge for those.
      const needsSubscriptionOnetimesRefetch =
        bundleWrote || hasPlanChanged || anyAddonMutated || deletedOnetimeCount > 0;
      // Charges totals change when the bundle or any onetime write landed
      // (including flushed click-time upserts). True no-ops skip charges too.
      const chargesMayHaveChanged =
        needsSubscriptionOnetimesRefetch || flushedCount > 0;

      // Optimistic unlock: writes are done, so drop the saving spinner and
      // toast immediately. The refetch below (when needed) no longer blocks UI.
      const { saveTimeoutId: earlyTimeoutId } = get();
      if (earlyTimeoutId) {
        clearTimeout(earlyTimeoutId);
      }
      set({
        isSaving: false,
        hasChanges: false,
        saveError: false,
        justSaved: true,
        saveTimeoutId: null,
        addonSyncStatus: {},
      });
      // New look: the bottom editing bar already walks the member through
      // "Saving your box… → Box saved" on auto-save, so a toast on top of it
      // (right after the "added!" toast) was one confirmation too many. An
      // explicit Save tap still gets the toast; the old look keeps it always.
      if (!(isAutoSave && isTestVersionActive())) {
        notification.success('Your selection has been saved');
      }

      let updatedSubscription;
      let freshOnetimes;
      if (needsSubscriptionOnetimesRefetch) {
        try {
          const results = await Promise.all([
            fetchSubscription(subscription.id),
            fetchOnetimes()
          ]);
          updatedSubscription = results[0] || subscription;
          freshOnetimes = Array.isArray(results[1]) ? results[1] : [];
        } catch (refreshError) {
          // Writes already succeeded — don't fail the save on a refresh blip.
          // Leave lastSaveRefresh empty so Overview re-fetches for real.
          console.error('Post-save subscription/onetimes refresh failed:', refreshError);
          updatedSubscription = get().subscription || subscription;
          freshOnetimes = Array.isArray(get().onetimes) ? get().onetimes : onetimes;
        }
      } else {
        updatedSubscription = get().subscription || subscription;
        freshOnetimes = Array.isArray(get().onetimes) ? get().onetimes : onetimes;
      }
      const freshBundleItems = updatedSubscription?.include?.bundle_selections?.items || [];
      
      // Get the current state (which may include products added during saving)
      const currentState = get();
      const currentSelectedProducts = currentState.selectedProducts;
      
      // Merge the saved products with any products added during saving
      const mergedProducts = [];
      const addedProductIds = new Set();
      const productsAddedDuringSave = [];
      
      // First, add all successfully saved products
      selectedProducts.forEach(product => {
        mergedProducts.push(product);
        addedProductIds.add(product.id + (product.isAddon ? '_addon' : '_plan'));
      });
      
      // Then, identify products that were added during saving
      currentSelectedProducts.forEach(product => {
        const productKey = product.id + (product.isAddon ? '_addon' : '_plan');
        if (!addedProductIds.has(productKey)) {
          // This product was added during saving, include it and mark for saving
          mergedProducts.push(product);
          productsAddedDuringSave.push(product);
        }
      });
      
      // If there are products that were added during saving, save them immediately
      // Rows created here happen AFTER the freshOnetimes refetch above, so they
      // must be captured and merged into the store's onetimes below — losing
      // them is how duplicate rows used to get created (the next write for the
      // product couldn't see its existing row).
      const midSaveCreatedRows = [];
      if (productsAddedDuringSave.length > 0) {
        
        // Process addons that were added during saving (limited concurrency).
        const addonsAddedDuringSave = productsAddedDuringSave.filter(p => p.isAddon && !p.isFree);
        const midSaveRows = await mapWithConcurrency(
          addonsAddedDuringSave,
          ONETIME_WRITE_CONCURRENCY,
          async (addon) => {
            try {
              const onetimeProps = Array.isArray(addon.properties) ? addon.properties : [];
              const bonusProps = addon.isTierBonus
                ? [{ name: '_tier_bonus', value: String(addon.tierId || 'unknown') }]
                : [];
              const combinedProps = [...onetimeProps, ...bonusProps];
              const priceOverride = addon.isTierBonus ? '0.00' : null;
              const createResult = await createOnetimeItem(
                updatedSubscription.include.address.id,
                {
                  id: addon.id,
                  title: addon.title,
                  quantity: addon.quantity,
                  variants: addon.variants
                },
                updatedSubscription.next_charge_scheduled_at,
                combinedProps,
                priceOverride
              );
              const createdRow = createResult?.onetime || createResult;
              await paceOnetimeMutation();
              return createdRow?.id ? createdRow : null;
            } catch (error) {
              console.error(`Error saving addon added during save: ${addon.id}:`, error);
              // Don't throw - we want to continue with other products
              return null;
            }
          }
        );
        midSaveCreatedRows.push(...midSaveRows.filter(Boolean));
        
        // Process regular products that were added during saving
        const regularProductsAddedDuringSave = productsAddedDuringSave.filter(p => !p.isAddon && !p.isFree);
        if (regularProductsAddedDuringSave.length > 0) {
          try {
            // Get current bundle items and add the new regular products
            const currentBundleItems = [...freshBundleItems];
            
            regularProductsAddedDuringSave.forEach(product => {
              currentBundleItems.push({
                collection_id: product.collection_id || "",
                collection_source: 'shopify',
                external_product_id: String(product.id),
                external_variant_id: product.variants && product.variants.length > 0 ? String(product.variants[0].id) : "",
                quantity: product.quantity
              });
            });
            
            // Update bundle selection with the new items
            await updateBundleSelectionItems(
              updatedSubscription.include.bundle_selections.id,
              currentBundleItems
            );
          } catch (error) {
            console.error('Error saving regular products added during save:', error);
          }
        }
      }
      
      // The store's onetimes must mirror the server: the fresh post-mutation
      // list plus any rows created for mid-save additions (created after that
      // refetch). Rows that survived for products no longer selected (e.g. an
      // auto-save that skipped a deletion) stay in the list on purpose — the
      // immediate-removal path and the next manual save need to see them to
      // delete them.
      const finalOnetimes = [
        ...freshOnetimes,
        ...midSaveCreatedRows.filter(row => !freshOnetimes.some(ot => ot.id === row.id))
      ];

      // Keep pending removals whose rows still exist in Recharge (auto-saves
      // skip deletions by design; a failed delete also lands here). Wiping
      // them used to erase the member's removal: the row survived server-side
      // and the add-on came back on the next refresh.
      const remainingRemovedProductIds = removedProductIds.filter(removedId =>
        finalOnetimes.some(ot =>
          String(ot.external_product_id?.ecommerce || '') === String(removedId) ||
          String(ot.id) === String(removedId)
        )
      );

      // Update quantities based on merged products
      const updatedQuantities = {};
      mergedProducts.forEach(product => {
        updatedQuantities[product.id] = product.quantity || 1;
      });
      
      // Calculate totals based on merged products
      const { totalItems, totalServings } = calculateTotals(mergedProducts);
      
      // Update baselines / stash. isSaving was already cleared optimistically
      // above; keep it false here. Toast already fired.
      const midSaveDirty = productsAddedDuringSave.length > 0;
      set({ 
        isConfirmed: true,
        hasChanges: false,
        quantities: updatedQuantities,
        initialQuantities: { ...updatedQuantities },
        removedProductIds: remainingRemovedProductIds,
        clearedBoxThisSession: false,
        bundleItems: freshBundleItems,
        onetimes: finalOnetimes,
        selectedProducts: mergedProducts,
        subscription: updatedSubscription,
        totalItems,
        totalServings,
        isSaving: false,
        saveError: false,
        addonSyncStatus: {},
        justSaved: true,
        saveTimeoutId: null,
        // A committed save either carried the size switch through or the
        // member saved at the old size on purpose — nothing pending either way.
        pendingPlanSize: null,
        // Hand the save's subscription + onetimes to the post-save UI refresh
        // so it doesn't re-request them. Mid-save creations make that stash
        // stale — leave null so Overview fetches for real. skipCharges when
        // nothing price-affecting happened (avoids a charges round-trip).
        lastSaveRefresh: midSaveDirty
          ? null
          : {
              subscription: updatedSubscription,
              onetimes: finalOnetimes,
              savedAt: Date.now(),
              skipCharges: !chargesMayHaveChanged
            }
      });

      // Debounced Klaviyo event
      const customerEmail = updatedSubscription?.include?.customer?.email;
      if (customerEmail) {
        // Cancel any existing timeout
        const existingTimeoutId = get().klaviyoEventTimeoutId;
        if (existingTimeoutId) {
          clearTimeout(existingTimeoutId);
        }

        // Store the event data for deduplication
        const eventData = {
          email: customerEmail,
          subscriptionId: updatedSubscription.id,
          products: mergedProducts,
          nextChargeDate: updatedSubscription.next_charge_scheduled_at
        };

        // Set a new timeout for 10 seconds
        const newTimeoutId = setTimeout(async () => {
          try {
            const currentEventData = get().lastKlaviyoEventData;
            // Only send if the data hasn't changed (prevents duplicate events)
            if (currentEventData && currentEventData.email === eventData.email && currentEventData.subscriptionId === eventData.subscriptionId) {
              await trackBoxSelectionSaved(
                eventData.email,
                eventData.subscriptionId,
                eventData.products,
                eventData.nextChargeDate
              );
              // console.log('Klaviyo Box Selection Saved event sent after 15s debounce');

              // Clear the stored event data
              set({ lastKlaviyoEventData: null, klaviyoEventTimeoutId: null });
            }
          } catch (err) {
            console.error('Failed to send Klaviyo box selection event:', err);
          }
        }, 15000); // 15 second delay

        // Store the timeout ID and event data
        set({
          klaviyoEventTimeoutId: newTimeoutId,
          lastKlaviyoEventData: eventData
        });
      }

      return mergedProducts;
      
    } catch (error) {
      console.error('Error saving box selection:', error);

      // Failed saves surface through saveError / toasts — a lingering
      // per-row "Saving…" badge would contradict them.
      set({ addonSyncStatus: {} });

      const isAuthError = error?.status === 401 || String(error?.message || '').toLowerCase().includes('bad authentication');
      if (isAuthError) {
        try {
          localStorage.removeItem('rechargeSession');
        } catch {}
        window.skipUnsavedChangesPrompt = true;
        window.dispatchEvent(new Event('session-expired'));
        notification.error('Your session expired. Please log in again.');
        set({ isSaving: false, saveTimeoutId: null, klaviyoEventTimeoutId: null, lastKlaviyoEventData: null });
        return null;
      }

      // Check for pending charge error (status 409 or message containing "pending charge")
      const errorMessage = String(error?.message || '').toLowerCase();
      const isPendingChargeError = error?.status === 409 || errorMessage.includes('pending charge');
      if (isPendingChargeError) {
        window.dispatchEvent(new Event('pending-charge-error'));
        set({ isSaving: false, saveError: true, saveTimeoutId: null, klaviyoEventTimeoutId: null, lastKlaviyoEventData: null });
        return null;
      }

      // A failed payment on the address locks every box write in Recharge
      // ("Must remove/fix existing error charges first"). Retrying is
      // pointless — hand off to the payment-fix flow instead of the generic
      // "please try again" toast.
      if (isErrorChargeBlockingError(error)) {
        // A listener that opens the payment-fix modal calls preventDefault(),
        // which makes dispatchEvent return false — no toast needed then.
        const unhandled = window.dispatchEvent(new CustomEvent('charge-error-blocking', { cancelable: true }));
        if (unhandled) {
          notification.error(ERROR_CHARGE_BLOCKING_MESSAGE, { duration: 10000 });
        }
        const { subscription: blockedSub } = get();
        logPortalError('Box save blocked by errored charge', error, {
          subscriptionId: blockedSub?.id,
          customerId: blockedSub?.include?.customer?.id,
          customerEmail: blockedSub?.include?.customer?.email,
          addressId: blockedSub?.include?.address?.id,
          nextChargeDate: blockedSub?.next_charge_scheduled_at,
          isAutoSave,
        }).catch(logErr => {
          console.error('Failed to send error log:', logErr);
        });
        set({ isSaving: false, saveError: true, saveTimeoutId: null, klaviyoEventTimeoutId: null, lastKlaviyoEventData: null });
        return null;
      }

      // Check for Recharge service unavailability errors
      const isRechargeServiceError = 
        errorMessage.includes('external service product search unavailable') ||
        errorMessage.includes('load failed');
      if (isRechargeServiceError) {
        notification.loadError("We're having temporary issues loading your box.");
        set({ isSaving: false, saveError: true, saveTimeoutId: null, klaviyoEventTimeoutId: null, lastKlaviyoEventData: null });
        return null;
      }

      // Log detailed error information to backend for debugging
      const currentState = get();
      const debugContext = {
        subscriptionId: currentState.subscription?.id,
        customerId: currentState.subscription?.include?.customer?.id,
        customerEmail: currentState.subscription?.include?.customer?.email,
        bundleSelectionId: currentState.subscription?.include?.bundle_selections?.id,
        purchaseItemId: currentState.subscription?.include?.bundle_selections?.purchase_item_id,
        addressId: currentState.subscription?.include?.address?.id,
        nextChargeDate: currentState.subscription?.next_charge_scheduled_at,
        selectedProductsCount: currentState.selectedProducts?.length,
        bundleItemsCount: currentState.bundleItems?.length,
        onetimesCount: currentState.onetimes?.length,
        removedProductIdsCount: currentState.removedProductIds?.length,
        rechargeBundleVariantId,
        bundleVariantId,
        // Mirror the save path's Shopify-vs-Shopify comparison. The old
        // formula compared against the RECHARGE bundle variant id, which
        // never matches — every error logged hasPlanChanged: true.
        hasPlanChanged: Boolean(bundleVariantId) &&
          String(currentState.subscription?.include?.bundle_selections?.external_variant_id) !== String(bundleVariantId),
        selectedProductIds: currentState.selectedProducts?.map(p => ({ 
          id: p.id, 
          title: p.title, 
          isAddon: p.isAddon, 
          isFree: p.isFree,
          quantity: p.quantity 
        })),
      };
      
      // Recharge's bundle-size validation (422) is member-fixable: say what
      // is wrong instead of the generic retry prompt, which sent members
      // into save/fail loops. Logged under its own message so it stays
      // separable from real save failures in Mezmo.
      const sizeMismatch = parseBundleSizeError(error);
      // Same idea for an item Recharge no longer accepts in its collection:
      // name the item so the member can swap it, instead of a retry loop.
      const collectionMismatch = parseCollectionMismatchError(error, currentState.selectedProducts);
      // Recharge named the rows it will never accept for this plan: drop them
      // now so the member refills the slots, instead of the same 422 on every
      // auto-save (one member re-failed four times in 70s, Oct 2026).
      let mismatchDrop = null;
      if (collectionMismatch) {
        debugContext.rejectedItems = collectionMismatch.items;
        mismatchDrop = dropPlanItemsNotInPlan(collectionMismatch.items.map(i => i.productId));
        if (mismatchDrop.removed.length === 0) mismatchDrop = null;
        debugContext.removedFromBox = mismatchDrop ? mismatchDrop.removed : [];
      }

      // Send error log to backend (non-blocking)
      logPortalError(
        sizeMismatch
          ? 'Box selection rejected: bundle size mismatch'
          : collectionMismatch
            ? 'Box selection rejected: item not in plan collection'
            : 'Box selection save failed',
        error,
        debugContext
      ).catch(logErr => {
        console.error('Failed to send error log:', logErr);
      });

      if (sizeMismatch) {
        notification.error(sizeMismatch.message, { duration: 10000 });
      } else if (mismatchDrop) {
        notification.error(mismatchDrop.message, { duration: 12000 });
      } else if (collectionMismatch) {
        notification.error(collectionMismatch.message, { duration: 10000 });
      } else {
        notification.error('Failed to save your box selection. Please try again.');
      }
      
      // Clear the timeout since save failed
      const { saveTimeoutId, klaviyoEventTimeoutId } = get();
      if (saveTimeoutId) {
        clearTimeout(saveTimeoutId);
      }
      // Also clear any pending Klaviyo event on error
      if (klaviyoEventTimeoutId) {
        clearTimeout(klaviyoEventTimeoutId);
      }

      // Reset loading state on error
      set({ isSaving: false, saveError: true, saveTimeoutId: null, klaviyoEventTimeoutId: null, lastKlaviyoEventData: null });
      return null;
    }
  },
  
  // Cancel selection and revert to initial state
  cancelSelection: (showNotification = true) => {
    const { initialQuantities, bundleItems, onetimes, freeProduct, products } = get();
    
    // Reset quantities to initial state
    const quantities = { ...initialQuantities };
    
    // Recalculate selected products based on initial quantities
    const selectedProducts = [];
    
    // Add bundle items to selected products
    bundleItems.forEach(item => {
      const product = products.find(p => p.id === item.external_product_id);
      if (product && initialQuantities[item.external_product_id] > 0) {
        selectedProducts.push({
          ...product,
          quantity: initialQuantities[item.external_product_id]
        });
      }
    });
    
    // Add onetimes to selected products. Mirror the hydration in initialize()
    // so a previously-persisted tier bonus reappears as free after Cancel.
    onetimes.forEach(onetime => {
      const product = products.find(p => p.id === onetime.external_product_id.ecommerce);
      if (product && initialQuantities[onetime.id] > 0) {
        const props = Array.isArray(onetime.properties) ? onetime.properties : [];
        const tierBonusProp = props.find(p => p?.name === '_tier_bonus');
        const priceNum = parseFloat(onetime.price || 0);
        const isTierBonus = Boolean(tierBonusProp) && priceNum === 0;
        selectedProducts.push({
          ...product,
          quantity: initialQuantities[onetime.id],
          isAddon: true,
          price: isTierBonus ? 0 : onetime.price,
          isTierBonus,
          tierId: isTierBonus ? (tierBonusProp?.value || '') : ''
        });
      }
    });
    
    // Add free product to selected products if it exists
    if (freeProduct && initialQuantities[freeProduct.id] > 0) {
      selectedProducts.push({
        ...freeProduct,
        quantity: initialQuantities[freeProduct.id],
        isFree: true
      });
    }
    
    // Reset state
    set({ 
      selectedProducts,
      quantities,
      removedProductIds: [],
      hasChanges: false,
      saveError: false,
      pendingPlanSize: null
    });
    
    // Recalculate totals
    const { totalItems, totalServings } = calculateTotals(selectedProducts);
    
    set({ totalItems, totalServings });
    
    // Show notification
    if (showNotification) {
      notification.success('Changes reverted');
    }
  },
  
  // Get sorted selected products
  getSortedSelectedProducts: () => {
    const { selectedProducts } = get();
    
    // Keep products in their original order, only sort by type priority
    return [...selectedProducts].sort((a, b) => {
      // Free products first
      if (a.isFree && !b.isFree) return -1;
      if (!a.isFree && b.isFree) return 1;
      
      // Then regular products before addons
      if (!a.isAddon && b.isAddon) return -1;
      if (a.isAddon && !b.isAddon) return 1;
      
      // Keep original order for products of the same type
      return 0;
    });
  },
  
  // Remove a onetime product
  removeOnetime: (onetimeId) => {
    // Update quantities to zero for this onetime
    set(state => ({
      quantities: { ...state.quantities, [onetimeId]: 0 },
      selectedProducts: state.selectedProducts.filter(p => p.id !== onetimeId),
      removedProductIds: [...state.removedProductIds, onetimeId]
    }));
    
    // Recalculate totals and check for changes
    updateTotalsAndCheckChanges();
  },

  // Clear all plan items and addons (keep only free items)
  clearPlanItems: () => {
    // Clearing removes every add-on; any debounced click-time write for them
    // is now stale and must not fire.
    cancelAllPendingAddonUpserts();
    const { selectedProducts, bundleItems } = get();
    
    // Filter out all plan items and addons (keep only free items)
    const remainingProducts = selectedProducts.filter(p => p.isFree);
    
    // Update quantities to remove plan items and addons
    const newQuantities = {};
    remainingProducts.forEach(product => {
      newQuantities[product.id] = product.quantity || 1;
    });
    
    // Track removed plan items and addons for the API call
    const removedIds = selectedProducts
      .filter(p => !p.isFree)
      .map(p => p.id);
    
    // Add-ons leaving the box — one entry per distinct product + bonus-ness,
    // which is what the Recharge rows are keyed on.
    const removedAddons = [];
    selectedProducts.forEach(p => {
      if (!p.isAddon || p.isFree) return;
      const dup = removedAddons.some(r =>
        String(r.id) === String(p.id) && Boolean(r.isTierBonus) === Boolean(p.isTierBonus)
      );
      if (!dup) removedAddons.push(p);
    });
    
    set(state => ({
      selectedProducts: remainingProducts,
      quantities: newQuantities,
      removedProductIds: [...state.removedProductIds, ...removedIds],
      // Lets the save-time circuit breaker distinguish an intentional
      // clear-everything from a buggy mass deletion.
      clearedBoxThisSession: true,
      // Every add-on row just left the UI; no badges left to show.
      addonSyncStatus: {},
      hasChanges: true
    }));
    
    // Recalculate totals
    updateTotalsAndCheckChanges();
    
    notification.success('All items cleared');
    
    // Persist the add-on removals now, like a single add-on removal does.
    // The save-time diff is the only other path that deletes onetimes and it
    // is skipped on auto-saves — so a Clear followed by refilling the box
    // (which auto-saves) used to keep every add-on in Recharge, and they all
    // came back on reload. Runs after the state update so the "still
    // selected" check inside sees the cleared box.
    if (removedAddons.length > 0) {
      deleteOnetimeRowsForRemovedAddons(removedAddons);
    }
  }
}));

// Helper function to calculate totals based on a given list of selected products
const calculateTotals = (selectedProducts) => {
  let totalItems = 0;
  let totalServings = 0;

  // Iterate through the provided selected products to calculate totals
  selectedProducts.forEach(product => {
    // Ensure quantity is a number and positive
    const quantity = Number(product.quantity) || 0;
    if (quantity > 0) {
      totalItems += quantity;

      // Extract servings, only if not a free item (assuming free items don't count towards servings total)
      // If free items *should* count, remove the !product.isFree check
      const servings = extractServings(product.descriptionHtml);
      if (servings) {
        totalServings += servings * quantity;
      } else {
         console.warn(`Servings not found for product: ${product.title} (ID: ${product.id})`);
      }
    }
  });
  
  return { totalItems, totalServings };
};

// ---------------------------------------------------------------------------
// Plan-slot cap.
//
// Recharge rejects a bundle write whose plan-item total exceeds the plan's
// slot count (422 "Bundle exceeds the max number of items allowed"), and
// the save then fails on every retry until the member removes something.
// A product's FIRST add goes through BoxBuilder's isBoxFull conversion, but
// the "+" steppers on cards call updateQuantity directly, which had no cap.
// (StatusBar has an after-the-fact rollback, but it keys off
// lastClickedProduct, which an add-on click leaves pointing at an add-on
// row — so it silently did nothing in exactly the sessions that overflowed.)
// The guards mirror the first-add behaviour: the unit that doesn't fit
// becomes a paid add-on rather than an extra plan item.
//
// `planItemLimit` is published by BoxBuilder once the bundle config resolves
// (null before that and on curated boxes); with no limit there is no cap,
// exactly as before.

const planItemTotal = (selectedProducts) =>
  (selectedProducts || [])
    .filter((p) => !p.isAddon && !p.isFree)
    .reduce((sum, p) => sum + (Number(p.quantity) || 0), 0);

// { limit } when adding `increaseBy` plan units would exceed the plan's
// slot count, otherwise null.
// The plan size to cap against: BoxBuilder's published limit when it has
// one, else the size parsed from the subscription at hydration. Null only
// when neither is known (pre-config gift flows, curated boxes without a
// sized variant title) — and then there is no cap, exactly as before.
const effectivePlanItemLimit = (state) => {
  const published = Number(state.planItemLimit);
  if (Number.isFinite(published) && published > 0) return published;
  const fallback = Number(state.planItemLimitFallback);
  if (Number.isFinite(fallback) && fallback > 0) return fallback;
  // planItemLimitFallback is only stamped by a FULL hydration, but adds can
  // land before initialize() runs, and initialize's unsaved-edits early
  // return keeps a session's rows alive without restamping it. Derive from
  // the subscription itself so the cap never silently disarms while the plan
  // size is knowable (production: a 6-item box reached 7 plan rows on
  // current code — subscription 823988690, Oct 17).
  return derivePlanItemLimit(state.subscription);
};

// "6 items (most popular) / Variety Box | $99" → 6. Null when the title
// carries no item count (gift redemption placeholder, curated boxes).
export const derivePlanItemLimit = (subscription) => {
  const title = subscription?.variant_title || '';
  const match = String(title).match(/(\d+)\s*items?\b/i);
  const count = match ? parseInt(match[1], 10) : NaN;
  return Number.isFinite(count) && count > 0 ? count : null;
};

const planSlotOverflow = (state, increaseBy) => {
  const limit = effectivePlanItemLimit(state);
  if (!limit) return null;
  if (!(increaseBy > 0)) return null;
  return planItemTotal(state.selectedProducts) + increaseBy > limit ? { limit } : null;
};

// The row updateQuantity's matcher resolves `targetProductId` to — using the
// same rules as the setter, uniqueId first — when that row is a plan item;
// null when it is an add-on or the free item. Deciding from the resolved row
// rather than the caller's `isAddon` flag matters: the old-design plan card
// passed `isAddon: true` whenever the product also existed as an add-on, and
// a plan row added this session (uniqueId === product id) still matched
// first, so the increase landed on the plan row with no cap.
const findPlanRow = (selectedProducts, targetProductId, isAddon, price = null) => {
  const row = (selectedProducts || []).find((p) => {
    if (p.uniqueId === targetProductId) return true;
    // Never resolve to the free row: when a `<id>_plan` row shares the free
    // item's product id, matching the free row first made this return null —
    // and the plan-slot cap was silently skipped for that row's stepper.
    if (p.isFree) return false;
    if (String(p.id) !== targetProductId) return false;
    if (!!p.isAddon !== !!isAddon) return false;
    if (isAddon && price !== null && p.price !== price) return false;
    return true;
  });
  return row && !row.isAddon && !row.isFree ? row : null;
};

// Add-on price for a plan row being converted: the row's own price, else
// the catalog product's. addonUnitPrice normalises the three shapes a
// product can arrive in (Storefront { amount }, dollar strings, bundle-data
// integer cents) to a "14.00" string.
const resolveAddonPrice = (product, catalog) => {
  const sources = [product, (catalog || []).find((p) => String(p.id) === String(product?.id))];
  for (const source of sources) {
    const price = addonUnitPrice(source);
    if (price !== null) return price;
  }
  return null;
};

const addOverflowAsAddon = (product, limit, units) => {
  const { addProduct, products } = useBoxSelectionStore.getState();
  const price = resolveAddonPrice(product, products);
  if (price === null) {
    notification.error(
      `Your box is full (${limit} of ${limit} items). Remove an item to make room for "${product.title}".`
    );
    return;
  }
  const { uniqueId, quantity, isTierBonus, tierId, ...rest } = product;
  for (let i = 0; i < units; i += 1) {
    addProduct({ ...rest, isAddon: true, isFree: false, price, collection_id: '' });
  }
  notification.success(`Your plan is full! "${product.title}" has been added as an add-on.`);
};

// Recharge rejects bundle writes whose item total doesn't match the plan
// with a 422 whose message is a JSON string, e.g.
//   {"details":{"itemsCount":12,"numberOfItemsInBundle":13},
//    "message":"Bundle exceeds the max number of items allowed"}
//   {"details":{"itemsCount":8,"numberOfItemsInBundle":6},
//    "message":"Bundle is missing items to reach the max number of items allowed"}
// itemsCount is the plan's slot count, numberOfItemsInBundle what we sent.
// Returns null for any other error.
export const parseBundleSizeError = (error) => {
  const raw = String(error?.message || '');
  const lower = raw.toLowerCase();
  const exceeds = lower.includes('bundle exceeds the max number of items');
  const missing = lower.includes('bundle is missing items');
  if (!exceeds && !missing) return null;

  let planSize = null;
  let sent = null;
  try {
    const parsed = JSON.parse(raw);
    planSize = Number(parsed?.details?.itemsCount);
    sent = Number(parsed?.details?.numberOfItemsInBundle);
  } catch {
    // Message wasn't JSON — fall back to the generic wording below.
  }
  const haveCounts = Number.isFinite(planSize) && Number.isFinite(sent) && planSize > 0;
  const diff = haveCounts ? Math.abs(sent - planSize) : 0;
  const items = (n) => `${n} item${n === 1 ? '' : 's'}`;

  let message;
  if (exceeds) {
    message = haveCounts
      ? `Your box holds ${items(planSize)} but ${sent} are selected. Please remove ${items(diff)} and save again.`
      : 'Your box has more items than your plan allows. Please remove an item and save again.';
  } else {
    message = haveCounts
      ? `Your box needs ${items(planSize)} but only ${sent} ${sent === 1 ? 'is' : 'are'} selected. Please add ${items(diff)} and save again.`
      : 'Your box is missing items. Please fill every slot and save again.';
  }
  return { kind: exceeds ? 'exceeds' : 'missing', planSize, sent, message };
};

// Recharge validates every bundle item against the collection_id we send.
// When a product is no longer in that collection (rotated out of FEATURED /
// CUSTOM_BOX, discontinued, or a stale id on its Recharge row) the write is
// rejected with a 422 whose message is a JSON string, e.g.
//   {"details":[{"collectionId":276262748227,"productId":7359005098051,
//     "variantId":42039875108931}],
//    "message":"At least one of the products wasn't found in its correspondent collection"}
// Returns null for any other error. `selectedProducts` is only used to turn
// the product ids into titles for the member-facing copy.
export const parseCollectionMismatchError = (error, selectedProducts = []) => {
  const raw = String(error?.message || '');
  if (!raw.toLowerCase().includes("wasn't found in its correspondent collection")) return null;

  let details = [];
  try {
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed?.details)) details = parsed.details;
  } catch {
    // Message wasn't JSON — fall back to the generic wording below.
  }

  const items = details.map(d => {
    const productId = String(d?.productId ?? '');
    const product = (selectedProducts || []).find(p => String(p?.id) === productId);
    return {
      productId,
      collectionId: String(d?.collectionId ?? ''),
      title: product?.title || null,
    };
  });
  const titles = [...new Set(items.map(i => i.title).filter(Boolean))];

  let message;
  if (titles.length === 1) {
    message = `"${titles[0]}" is no longer available for your plan, so your box can't be saved. Please swap it for another item and save again.`;
  } else if (titles.length > 1) {
    message = `${titles.map(t => `"${t}"`).join(', ')} are no longer available for your plan, so your box can't be saved. Please swap them for other items and save again.`;
  } else {
    message = "One of the items in your box is no longer available for your plan, so your box can't be saved. Please swap it for another item and save again.";
  }
  return { items, titles, message };
};

// Removes plan rows (never add-ons or the free item) for the given product
// ids from the box, through the store's own removal path so quantities and
// removedProductIds stay consistent, and marks the box as edited-but-unsaved
// so the member's next pick goes through the normal save. Returns what was
// removed plus the member-facing copy. Used by the pre-send guard in
// confirmSelection and by its handling of Recharge's collection 422.
export const dropPlanItemsNotInPlan = (productIds = []) => {
  const removed = [];
  [...new Set((productIds || []).map(id => String(id)))].forEach(productId => {
    const { selectedProducts, updateQuantity } = useBoxSelectionStore.getState();
    const row = selectedProducts.find(p => !p.isAddon && !p.isFree && String(p.id) === productId);
    if (!row) return;
    updateQuantity(row.id, 0, false);
    removed.push({ productId, title: row.title || null, quantity: row.quantity || 0 });
  });
  if (removed.length > 0) {
    updateTotals();
    useBoxSelectionStore.setState({ hasChanges: true, isConfirmed: false });
  }
  const titles = [...new Set(removed.map(r => r.title).filter(Boolean))];
  const quoted = titles.map(t => `"${t}"`).join(', ');
  const message = titles.length === 1
    ? `${quoted} is no longer available for your plan, so we removed it from your box. Please pick a replacement and save again.`
    : titles.length > 1
      ? `${quoted} are no longer available for your plan, so we removed them from your box. Please pick replacements and save again.`
      : 'An item in your box is no longer available for your plan, so we removed it. Please pick a replacement and save again.';
  return { removed, message };
};

// Function to update totals in the store
const updateTotals = () => {
  const { selectedProducts } = useBoxSelectionStore.getState();
  
  const { totalItems, totalServings } = calculateTotals(selectedProducts);
  
  useBoxSelectionStore.setState({ totalItems, totalServings });
};

// ---------------------------------------------------------------------------
// Click-time add-on persistence.
//
// Adding a paid add-on (or changing its quantity) schedules a debounced
// create/update of its Recharge onetime right away, instead of waiting for
// the save flow. By the time the auto-save fires, the row already matches
// the selection, so the save's add-on loop no-ops and the save is fast.
//
// Scope is deliberately narrow — regular PAID add-ons only:
//  - tier bonuses stay on the save path (they need the $0 admin proxy and
//    delete/recreate handling),
//  - removals are handled by deleteOnetimeRowsForRemovedAddon,
//  - plan items always go through the bundle PUT on save.
//
// Coordination rules that keep this safe:
//  - one pending timer per product id; re-clicks reset the debounce,
//  - confirmSelection flushes all pending timers (runs them now) and awaits
//    in-flight writes before diffing, so a save never races or duplicates an
//    upsert — and the save loop can no-op add-ons that are already synced,
//  - the upsert re-reads store state when it fires and skips if the add-on
//    was removed meanwhile, a save started, or hydration is incomplete,
//  - failures are console-only: state still differs from Recharge, so the
//    normal save path retries the write.
// ---------------------------------------------------------------------------
const ADDON_UPSERT_DEBOUNCE_MS = 800;
// Brief pause between onetime mutations to avoid Recharge rate-limit bursts.
// Was 300ms; flushed click-time upserts already cover the common case, so
// the save path rarely pays this more than once or twice.
const ONETIME_MUTATION_PACING_MS = 100;
const paceOnetimeMutation = () =>
  ONETIME_MUTATION_PACING_MS > 0
    ? new Promise((resolve) => setTimeout(resolve, ONETIME_MUTATION_PACING_MS))
    : Promise.resolve();
// Cap parallel onetime create/update/delete calls so a box with many add-on
// edits doesn't serialize every Recharge round-trip, without opening the
// floodgates on rate limits.
const ONETIME_WRITE_CONCURRENCY = 3;
const mapWithConcurrency = async (items, concurrency, worker) => {
  if (!items.length) return [];
  const results = new Array(items.length);
  let nextIndex = 0;
  const workerCount = Math.min(Math.max(concurrency, 1), items.length);
  await Promise.all(
    Array.from({ length: workerCount }, async () => {
      while (nextIndex < items.length) {
        const index = nextIndex;
        nextIndex += 1;
        results[index] = await worker(items[index], index);
      }
    })
  );
  return results;
};
const pendingAddonUpsertTimers = new Map();
const inFlightAddonUpserts = new Set();

// Mirror a freshly created Recharge onetime row into store state immediately.
// Every create site MUST call this: the save path used to discard create
// responses, so when a save aborted on a later add-on (e.g. a 409 while
// Recharge regenerates the charge) the store never learned the row existed —
// the immediate-removal path found nothing to delete and the row survived as
// an orphan in Recharge (observed live 2026-08-09).
const registerOnetimeRowInStore = (result) => {
  const row = result?.onetime || result;
  if (!row?.id) return;
  useBoxSelectionStore.setState(state => ({
    onetimes: state.onetimes.some(ot => ot.id === row.id)
      ? state.onetimes
      : [...state.onetimes, row]
  }));
};

// Mirror a successful quantity update the same way, so an aborted save still
// leaves store state matching what Recharge actually holds.
const applyOnetimeQuantityInStore = (rowId, quantity, nextChargeDate = null) => {
  useBoxSelectionStore.setState(state => ({
    onetimes: state.onetimes.map(ot =>
      ot.id === rowId
        ? { ...ot, quantity, ...(nextChargeDate ? { next_charge_scheduled_at: nextChargeDate } : {}) }
        : ot
    )
  }));
};

// A onetime row we thought existed came back 404 — drop it from store state
// and tombstone it so a stale refresh can't resurrect it.
const dropGoneOnetimeFromStore = (rowId) => {
  recordOnetimeTombstone(rowId);
  useBoxSelectionStore.setState(state => ({
    onetimes: state.onetimes.filter(ot => ot.id !== rowId)
  }));
};

const hasTierBonusProp = (ot) => {
  const props = Array.isArray(ot?.properties) ? ot.properties : [];
  return props.some(p => p?.name === '_tier_bonus');
};

// True when Recharge says the onetime row we tried to touch no longer exists.
// Observed live (2026-08-09): Recharge consolidated duplicate rows server-side
// and a PUT against the locally cached id came back 404, failing the whole
// save. A missing row should be recreated, not treated as a fatal error.
const isOnetimeGoneError = (error) =>
  error?.status === 404 || /not found/i.test(String(error?.message || ''));

// Tombstones for onetime rows this session has deleted (or confirmed gone).
// Data refreshes are async, so an `onetimes` list handed to initialize() can
// predate a deletion — without tombstones, re-initializing from such a list
// resurrects the removed add-on in the drawer (and a later save would then
// recreate it in Recharge). Session-scoped and TTL'd: after the TTL any row
// still on the server is honored again as the truth.
const ONETIME_TOMBSTONE_TTL_MS = 10 * 60 * 1000;
const onetimeTombstones = new Map();
const recordOnetimeTombstone = (rowId) => {
  if (rowId === undefined || rowId === null) return;
  onetimeTombstones.set(String(rowId), Date.now());
};
const isTombstonedOnetime = (rowId) => {
  const deletedAt = onetimeTombstones.get(String(rowId));
  if (!deletedAt) return false;
  if (Date.now() - deletedAt > ONETIME_TOMBSTONE_TTL_MS) {
    onetimeTombstones.delete(String(rowId));
    return false;
  }
  return true;
};

// Per-add-on sync indicator ('saving' | 'saved') keyed by product id, shown
// on the Your Box rows. 'saved' auto-clears after a short beat so the row
// returns to its quiet resting state.
const ADDON_SAVED_BADGE_MS = 3000;
const addonSavedClearTimers = new Map();
const setAddonSyncStatus = (productId, status) => {
  const key = String(productId);
  const pendingClear = addonSavedClearTimers.get(key);
  if (pendingClear) {
    clearTimeout(pendingClear);
    addonSavedClearTimers.delete(key);
  }
  useBoxSelectionStore.setState(state => {
    if (!status && !(key in state.addonSyncStatus)) return {};
    const next = { ...state.addonSyncStatus };
    if (status) {
      next[key] = status;
    } else {
      delete next[key];
    }
    return { addonSyncStatus: next };
  });
  if (status === 'saved') {
    addonSavedClearTimers.set(key, setTimeout(() => {
      addonSavedClearTimers.delete(key);
      useBoxSelectionStore.setState(state => {
        if (state.addonSyncStatus[key] !== 'saved') return {};
        const next = { ...state.addonSyncStatus };
        delete next[key];
        return { addonSyncStatus: next };
      });
    }, ADDON_SAVED_BADGE_MS));
  }
};

const cancelPendingAddonUpsert = (productId) => {
  const key = String(productId);
  const timer = pendingAddonUpsertTimers.get(key);
  if (timer) {
    clearTimeout(timer);
    pendingAddonUpsertTimers.delete(key);
  }
};

const cancelAllPendingAddonUpserts = () => {
  for (const timer of pendingAddonUpsertTimers.values()) {
    clearTimeout(timer);
  }
  pendingAddonUpsertTimers.clear();
};

const awaitInFlightAddonUpserts = async () => {
  if (inFlightAddonUpserts.size === 0) return;
  await Promise.allSettled([...inFlightAddonUpserts]);
};

// Run every debounced click-time upsert immediately (in parallel), then wait
// for those plus any already in-flight writes. Used by confirmSelection so
// the save loop sees freshly-synced onetime rows and skips re-writing them.
// clearPlanItems still uses cancelAllPendingAddonUpserts — those rows were
// removed and must not be created.
const flushPendingAddonUpserts = async () => {
  const pendingKeys = [...pendingAddonUpsertTimers.keys()];
  for (const key of pendingKeys) {
    const timer = pendingAddonUpsertTimers.get(key);
    if (timer) clearTimeout(timer);
    pendingAddonUpsertTimers.delete(key);
    setAddonSyncStatus(key, 'saving');
    const run = upsertOnetimeForAddon(key);
    inFlightAddonUpserts.add(run);
    run.finally(() => inFlightAddonUpserts.delete(run));
  }
  await awaitInFlightAddonUpserts();
  return pendingKeys.length;
};

const scheduleAddonUpsert = (product) => {
  if (!product?.isAddon || product.isFree || product.isTierBonus) return;
  const { hydrationIncomplete, subscription } = useBoxSelectionStore.getState();
  if (hydrationIncomplete) return;
  // No live Recharge target (e.g. gift redemption's pseudo-subscription):
  // nothing to persist at click time, so don't start a timer or show a badge.
  if (!subscription?.include?.address?.id) return;

  const key = String(product.id);
  cancelPendingAddonUpsert(key);
  setAddonSyncStatus(key, 'saving');
  pendingAddonUpsertTimers.set(key, setTimeout(() => {
    pendingAddonUpsertTimers.delete(key);
    const run = upsertOnetimeForAddon(key);
    inFlightAddonUpserts.add(run);
    run.finally(() => inFlightAddonUpserts.delete(run));
  }, ADDON_UPSERT_DEBOUNCE_MS));
};

// Create or quantity-update the Recharge onetime for one paid add-on so it
// matches the current selection. Mirrors the save path's payloads exactly.
const upsertOnetimeForAddon = async (productId) => {
  const { onetimes, selectedProducts, subscription, hydrationIncomplete, isSaving } =
    useBoxSelectionStore.getState();
  // A running save owns all mutations; its diff will pick this change up and
  // the save's completion clears the status map — keep 'saving' showing.
  if (isSaving) return;
  if (hydrationIncomplete) {
    setAddonSyncStatus(productId, null);
    return;
  }

  const addressId = subscription?.include?.address?.id;
  const nextChargeDate = subscription?.next_charge_scheduled_at;
  if (!addressId) {
    setAddonSyncStatus(productId, null);
    return;
  }

  const instances = selectedProducts.filter(p =>
    p.isAddon && !p.isFree && !p.isTierBonus && String(p.id) === String(productId)
  );
  // 0 instances: removed while debouncing — the removal path owns the row.
  // >1 instances (same product at different prices): ambiguous against a
  // single Recharge row, defer to the save path like before.
  if (instances.length !== 1) {
    setAddonSyncStatus(productId, null);
    return;
  }
  const product = instances[0];

  const existing = onetimes.find(ot =>
    String(ot.external_product_id?.ecommerce || '') === String(product.id) &&
    !hasTierBonusProp(ot)
  );

  try {
    // When the cached row turns out to be gone server-side (404 on update),
    // drop it locally and fall through to the create path below.
    let rowGone = false;
    if (existing) {
      if (
        existing.quantity === product.quantity &&
        (!nextChargeDate || sameChargeDay(existing.next_charge_scheduled_at, nextChargeDate))
      ) {
        // Already in sync — that IS saved, show it as such. A date mismatch
        // (row stranded on an old charge date after a reschedule) counts as
        // out of sync and falls through to the update below.
        setAddonSyncStatus(productId, 'saved');
        return;
      }
      try {
        await updateOnetime(existing.id, {
          quantity: product.quantity,
          next_charge_scheduled_at: nextChargeDate
        });
      } catch (updateError) {
        if (!isOnetimeGoneError(updateError)) throw updateError;
        rowGone = true;
        console.warn(`Box edit: onetime ${existing.id} no longer exists in Recharge; recreating.`);
        dropGoneOnetimeFromStore(existing.id);
      }
      if (!rowGone) {
        applyOnetimeQuantityInStore(existing.id, product.quantity, nextChargeDate);
        console.log(`Box edit: updated onetime ${existing.id} (${product.title}) to quantity ${product.quantity}`);
      }
    }
    if (!existing || rowGone) {
      const onetimeProps = Array.isArray(product.properties) ? product.properties : [];
      // Register the new row locally so the save diff, removal path, and
      // change detection all see it without waiting for a refetch.
      registerOnetimeRowInStore(await createOnetimeItem(
        addressId,
        {
          id: product.id,
          title: product.title,
          quantity: product.quantity,
          variants: product.variants
        },
        nextChargeDate,
        onetimeProps,
        null
      ));
      console.log(`Box edit: created onetime for ${product.title} (qty ${product.quantity}) at click time`);
    }
    setAddonSyncStatus(productId, 'saved');
  } catch (error) {
    // Leave state as-is: the selection still differs from Recharge, so the
    // auto-save (2s) or a manual save retries this write through the normal
    // save loop. Drop the badge rather than showing a stale "Saving…".
    setAddonSyncStatus(productId, null);
    console.error(`Error persisting add-on ${productId} at click time:`, error);
  }
};

// Background healer for duplicate Recharge onetime rows found at hydration:
// deletes the extra rows and fixes the survivor's quantity to the merged sum
// the drawer now displays. Conservative by design — caps how many rows one
// group may delete and tolerates already-gone rows. Detections are
// console-only: healing is routine self-repair, not an error worth paging on.
const MAX_DUPLICATE_ROWS_HEALED_PER_GROUP = 3;
let duplicateHealInFlight = false;
const healDuplicateOnetimeRows = async (groups) => {
  if (duplicateHealInFlight) return;
  duplicateHealInFlight = true;
  try {
    for (const { survivor, extras } of groups) {
      const { isSaving, subscription } = useBoxSelectionStore.getState();
      // A running save already converges the survivor's quantity; don't race it.
      if (isSaving) return;

      const productTitle = survivor.product_title || survivor.external_product_id?.ecommerce;
      console.warn(`Box hydrate: duplicate onetime rows for ${productTitle} (survivor ${survivor.id}, extras ${extras.map(r => r.id).join(', ')}) on subscription ${subscription?.id} — healing.`);

      if (extras.length > MAX_DUPLICATE_ROWS_HEALED_PER_GROUP) {
        console.error(`Box hydrate: ${extras.length} duplicate rows for ${productTitle} exceeds the healing cap — leaving them for manual review.`);
        continue;
      }

      let deletedQty = 0;
      let allExtrasGone = true;
      for (const row of extras) {
        try {
          await deleteOnetime(row.id);
          recordOnetimeTombstone(row.id);
          deletedQty += row.quantity || 0;
          console.log(`Box hydrate: deleted duplicate onetime ${row.id} (${productTitle})`);
          useBoxSelectionStore.setState(state => ({
            onetimes: state.onetimes.filter(ot => ot.id !== row.id)
          }));
          await paceOnetimeMutation();
        } catch (error) {
          if (isOnetimeGoneError(error)) {
            // Already gone server-side — drop it locally, but don't add its
            // quantity to the survivor (it no longer contributes anything).
            recordOnetimeTombstone(row.id);
            useBoxSelectionStore.setState(state => ({
              onetimes: state.onetimes.filter(ot => ot.id !== row.id)
            }));
            continue;
          }
          allExtrasGone = false;
          console.error(`Box hydrate: failed to delete duplicate onetime ${row.id}:`, error);
        }
      }

      // Fold the deleted rows' quantity into the survivor so the total the
      // member sees (and was paying for) is preserved by the merge.
      const targetQty = (survivor.quantity || 0) + deletedQty;
      if (allExtrasGone && deletedQty > 0 && targetQty !== survivor.quantity) {
        try {
          await updateOnetime(survivor.id, {
            quantity: targetQty,
            next_charge_scheduled_at: subscription?.next_charge_scheduled_at
          });
          useBoxSelectionStore.setState(state => ({
            onetimes: state.onetimes.map(ot =>
              ot.id === survivor.id ? { ...ot, quantity: targetQty } : ot
            )
          }));
          console.log(`Box hydrate: consolidated ${productTitle} into onetime ${survivor.id} (qty ${targetQty})`);
        } catch (error) {
          console.error(`Box hydrate: failed to consolidate quantity onto onetime ${survivor.id}:`, error);
        }
      }
    }
  } finally {
    duplicateHealInFlight = false;
  }
};

// Delete the Recharge onetime row(s) backing an add-on the member just
// removed. Called from updateQuantity's removal branch (and, for every
// add-on at once, from clearPlanItems) so explicit removals persist
// immediately — auto-saves never delete, so waiting for a save would only
// persist the removal on a manual Save click.
//
// Deliberately conservative: it only ever touches rows that match the exact
// product + bonus-ness of the removed instance, skips entirely while the
// store is partially hydrated, and skips if another selected instance still
// needs the row. When in doubt it deletes nothing — the manual-save diff
// (explicit removals only) remains as the backstop.
const deleteOnetimeRowsForRemovedAddon = async (removedProduct) => {
  // If a click-time upsert for this product is still debouncing, drop it —
  // it must not recreate the row after we delete it. If one is already in
  // flight, wait for it to land so its row is in store state and gets
  // deleted below rather than surviving as an orphan. The row leaves the UI
  // with the removal, so its sync badge goes too.
  cancelPendingAddonUpsert(removedProduct.id);
  setAddonSyncStatus(removedProduct.id, null);
  await awaitInFlightAddonUpserts();

  const { onetimes, selectedProducts, hydrationIncomplete } = useBoxSelectionStore.getState();
  if (hydrationIncomplete) return;

  const productId = String(removedProduct.id);
  const removedIsBonus = Boolean(removedProduct.isTierBonus);

  const isBonusOnetime = (ot) => {
    const props = Array.isArray(ot?.properties) ? ot.properties : [];
    return props.some(p => p?.name === '_tier_bonus');
  };

  // The same product can be in the box as both a paid row and a $0 bonus row,
  // or as multiple instances. If any selected instance still matches this
  // product + bonus-ness, its Recharge row must survive.
  const stillSelected = selectedProducts.some(p =>
    p.isAddon && !p.isFree &&
    String(p.id) === productId &&
    Boolean(p.isTierBonus) === removedIsBonus
  );
  if (stillSelected) return;

  const rows = onetimes.filter(ot =>
    String(ot.external_product_id?.ecommerce || '') === productId &&
    isBonusOnetime(ot) === removedIsBonus
  );

  for (const row of rows) {
    try {
      await deleteOnetime(row.id);
      recordOnetimeTombstone(row.id);
      // Intentional single removals are routine — console-only. The backend
      // only hears about the rare/suspicious cases: circuit-breaker trips and
      // save-path deletions.
      console.log(`Box edit: deleted onetime ${row.id} (${row.product_title}) after add-on removal`);
      useBoxSelectionStore.setState(state => ({
        onetimes: state.onetimes.filter(ot => ot.id !== row.id)
      }));
      await paceOnetimeMutation();
    } catch (error) {
      console.error(`Error deleting onetime ${row.id} for removed add-on ${productId}:`, error);
      notification.error(`We couldn't remove ${removedProduct.title || 'that add-on'}. Please save your box to try again.`);
      return;
    }
  }
};

// "Clear box" variant of the above: the member removed every add-on at once.
// Rows are deleted one product after another through the same conservative
// path (a failure on one row stops that product only; the rest proceed), and
// the whole clear is reported as ONE aggregated log entry — like save-time
// deletions, this is a path behind "my add-ons disappeared" reports, so it
// needs to be visible in the backend log.
const deleteOnetimeRowsForRemovedAddons = async (removedProducts) => {
  const { onetimes, hydrationIncomplete, subscription } = useBoxSelectionStore.getState();
  if (hydrationIncomplete || removedProducts.length === 0) return;

  const removedProductIds = new Set(removedProducts.map(p => String(p.id)));
  const rows = onetimes.filter(ot => removedProductIds.has(String(ot.external_product_id?.ecommerce || '')));
  if (rows.length > 0) {
    logPortalError('Box clear - deleting onetimes', new Error('Onetimes deleted on clear box'), {
      subscriptionId: subscription?.id,
      deleting: rows.map(ot => ({
        onetimeId: ot.id,
        productId: ot.external_product_id?.ecommerce,
        productTitle: ot.product_title,
      })),
    }).catch(() => {});
  }

  for (const removedProduct of removedProducts) {
    await deleteOnetimeRowsForRemovedAddon(removedProduct);
  }
};

// Helper function to check for changes and update state
const checkForChanges = () => {
  const { quantities, initialQuantities, removedProductIds, selectedProducts, onetimes } = useBoxSelectionStore.getState();
  
  // If any products were removed, there are changes
  if (removedProductIds.length > 0) {
    useBoxSelectionStore.setState({ hasChanges: true });
    return;
  }
  
  // Compare initial onetimes count with current onetimes
  const initialOnetimesCount = onetimes.length;
  const currentOnetimesCount = selectedProducts.filter(p => p.isAddon).length;
  
  if (initialOnetimesCount !== currentOnetimesCount) {
    useBoxSelectionStore.setState({ hasChanges: true });
    return;
  }
  
  // Check if quantities differ from initial quantities
  const allKeys = new Set([...Object.keys(quantities), ...Object.keys(initialQuantities)]);
  let hasChanges = false;
  
  for (const key of allKeys) {
    const initialQty = initialQuantities[key] || 0;
    const currentQty = quantities[key] || 0;
    
    if (initialQty !== currentQty) {
      hasChanges = true;
      break;
    }
  }
  
  useBoxSelectionStore.setState({ hasChanges });
};

// Combined function to update totals and check for changes in one operation
const updateTotalsAndCheckChanges = () => {
  // First update the totals
  updateTotals();
  
  // Then check for changes
  checkForChanges();

  // Any new edit clears a previous save failure — the member changed their
  // box, so the failed attempt is stale and auto-save can take over again.
  if (useBoxSelectionStore.getState().saveError) {
    useBoxSelectionStore.setState({ saveError: false });
  }
};

export default useBoxSelectionStore; 